import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { DecorativeIconPicker } from "@/components/DecorativeIconPicker";
import { getDefaultDepartmentIconKey } from "@/lib/decorativeIcons";
import { AppLayout } from "@/components/AppLayout";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeaderSkeleton, StatCardsSkeleton, CardGridSkeleton } from "@/components/PageSkeleton";
import { TeamsTab } from "@/components/organization/TeamsTab";
import { BulkImportDialog } from "@/components/BulkImportDialog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/DatePicker";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { COUNTRIES } from "@/lib/countries";
import {
  apiGetCompanyDetails, apiUpdateCompanyDetails, apiGetOrgStats,
  apiGetEmployees, apiGetEmployee, apiCreateEmployee, apiChangeEmployeeStatus, apiDeactivateEmployee,
  apiUpdateEmployee, apiGetDepartments, apiCreateDepartment, apiUpdateDepartment, apiDeleteDepartment,
  apiUploadCompanyLogo, apiUploadQuickLinkFile, getTenantSlug,
  apiGetEmployeeUserRole, apiUpdateEmployeeUserRole, apiGetTeamNames,
  apiGetSsoConfig, apiSaveSsoConfig, API_BASE,
  apiGetEmploymentDetails, apiUpdateEmploymentDetails, apiGetPositions, apiAssignEmployeePosition,
  apiCreatePosition, apiUpdatePosition, apiDeletePosition,
  apiAddPositionRequirement, apiRemovePositionRequirement,
  apiGetCertificateTypes, apiCreateCertificateType, apiDeleteCertificateType,
  apiGetTrainingTypes, apiCreateTrainingType, apiDeleteTrainingType,
  apiAddPositionTrainingRequirement, apiRemovePositionTrainingRequirement,
  type CompanyDetails, type QuickLink, type OrgStats, type EmployeeListItem, type CreateEmployeePayload,
  type UpdateEmployeePayload, type Department, type EmployeeUserRole, type SsoConfig, type EmploymentDetails,
  type Position, type CertificateType, type TrainingType,
} from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Building2, Users, Users2, UserPlus, Search, Save, Pencil, Trash2, Crown,
  CheckCircle2, Clock, AlertTriangle, XCircle, Briefcase,
  MapPin, Calendar, Globe, Phone, Mail, Hash, FileText,
  TrendingUp, UserMinus, Sparkles, Plus, X, ExternalLink, KeyRound,
  LayoutDashboard, Layers, ChevronRight,
  BookOpen, Shield, UserCheck, Star, BarChart3, Home, Award, Zap, Link2, ShieldCheck,
  GraduationCap, Loader2, Upload, Wallet, Check,
  User, DollarSign, FolderOpen, CalendarDays, MessageSquare, History,
} from "lucide-react";
import { Link } from "react-router-dom";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { ProfileHeader } from "@/components/ProfileHeader";
import { ProfileAboutTab } from "@/components/profile/ProfileAboutTab";
import { ProfilePersonalInfoTab } from "@/components/profile/ProfilePersonalInfoTab";
import { ProfilePerformanceTab } from "@/components/profile/ProfilePerformanceTab";
import { ProfileSalaryTab } from "@/components/profile/ProfileSalaryTab";
import { ProfileDocumentsTab } from "@/components/profile/ProfileDocumentsTab";
import { ProfileTimeOffTab } from "@/components/profile/ProfileTimeOffTab";
import { ProfileNotesTab } from "@/components/profile/ProfileNotesTab";
import { ProfileAuditTab } from "@/components/profile/ProfileAuditTab";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { FloatField, FloatWrapper } from "@/components/ui/FormField";
import {
  DEFAULT_EMPLOYMENT_CONTRACT_TYPE,
  EMPLOYMENT_CONTRACT_TYPES,
  type EmploymentContractTypeValue,
  getContractTypeLabel,
  isFixedTermContractType,
  isFullTimeContractType,
  isPartTimeContractType,
  calculatePartTimeWeeklyHours,
} from "@/lib/employment-contract-types";

const PIE_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4", "#ef4444", "#14b8a6"];

function getPartTimeHoursPerDayError(value: number | string | null | undefined, t: any): string {
  if (value === null || value === undefined || value === "") {
    return t("organization.addEmployeeEmployment.partTimeHoursPerDayRequired");
  }
  const hours = Number(value);
  if (!Number.isFinite(hours)) return t("organization.addEmployeeEmployment.partTimeHoursPerDayInvalid");
  if (hours <= 0) return t("organization.addEmployeeEmployment.partTimeHoursPerDayPositive");
  if (hours >= 8) return t("organization.addEmployeeEmployment.partTimeHoursPerDayLessThanFullTime");
  return "";
}

// Default quick links — kept in sync with the fallback in Dashboard.tsx
const DEFAULT_QUICK_LINKS_ORG: QuickLink[] = [
  { id: "d1", label: "Employee Handbook", linkType: "url", href: "#", icon: "BookOpen", color: "orange" },
  { id: "d2", label: "Onboarding Page", linkType: "internal", href: "/onboarding", icon: "UserCheck", color: "blue" },
  { id: "d3", label: "Time Off & Attendance", linkType: "internal", href: "/leave-management", icon: "Clock", color: "green" },
  { id: "d4", label: "Payroll & Benefits", linkType: "internal", href: "/my-salary", icon: "FileText", color: "purple" },
  { id: "d5", label: "IT Security", linkType: "url", href: "#", icon: "Shield", color: "red" },
  { id: "d6", label: "Company Intranet", linkType: "url", href: "#", icon: "Globe", color: "cyan" },
];

const TIMEZONES = [
  { value: "UTC", label: "UTC — Coordinated Universal Time" },
  // Europe
  { value: "Europe/London", label: "London (GMT / BST)" },
  { value: "Europe/Dublin", label: "Dublin (GMT / IST)" },
  { value: "Europe/Lisbon", label: "Lisbon (WET / WEST)" },
  { value: "Europe/Paris", label: "Paris (CET / CEST)" },
  { value: "Europe/Berlin", label: "Berlin (CET / CEST)" },
  { value: "Europe/Amsterdam", label: "Amsterdam (CET / CEST)" },
  { value: "Europe/Brussels", label: "Brussels (CET / CEST)" },
  { value: "Europe/Rome", label: "Rome (CET / CEST)" },
  { value: "Europe/Madrid", label: "Madrid (CET / CEST)" },
  { value: "Europe/Stockholm", label: "Stockholm (CET / CEST)" },
  { value: "Europe/Warsaw", label: "Warsaw (CET / CEST)" },
  { value: "Europe/Vienna", label: "Vienna (CET / CEST)" },
  { value: "Europe/Zurich", label: "Zurich (CET / CEST)" },
  { value: "Europe/Prague", label: "Prague (CET / CEST)" },
  { value: "Europe/Budapest", label: "Budapest (CET / CEST)" },
  { value: "Europe/Skopje", label: "Skopje (CET / CEST)" },
  { value: "Europe/Belgrade", label: "Belgrade (CET / CEST)" },
  { value: "Europe/Zagreb", label: "Zagreb (CET / CEST)" },
  { value: "Europe/Sofia", label: "Sofia (EET / EEST)" },
  { value: "Europe/Bucharest", label: "Bucharest (EET / EEST)" },
  { value: "Europe/Athens", label: "Athens (EET / EEST)" },
  { value: "Europe/Helsinki", label: "Helsinki (EET / EEST)" },
  { value: "Europe/Kiev", label: "Kyiv (EET / EEST)" },
  { value: "Europe/Istanbul", label: "Istanbul (TRT)" },
  { value: "Europe/Moscow", label: "Moscow (MSK)" },
  // Americas
  { value: "America/New_York", label: "New York (EST / EDT)" },
  { value: "America/Chicago", label: "Chicago (CST / CDT)" },
  { value: "America/Denver", label: "Denver (MST / MDT)" },
  { value: "America/Los_Angeles", label: "Los Angeles (PST / PDT)" },
  { value: "America/Anchorage", label: "Anchorage (AKST / AKDT)" },
  { value: "America/Toronto", label: "Toronto (EST / EDT)" },
  { value: "America/Vancouver", label: "Vancouver (PST / PDT)" },
  { value: "America/Sao_Paulo", label: "São Paulo (BRT / BRST)" },
  { value: "America/Mexico_City", label: "Mexico City (CST / CDT)" },
  { value: "America/Bogota", label: "Bogotá (COT)" },
  { value: "America/Santiago", label: "Santiago (CLT / CLST)" },
  { value: "America/Buenos_Aires", label: "Buenos Aires (ART)" },
  // Asia & Pacific
  { value: "Asia/Dubai", label: "Dubai (GST)" },
  { value: "Asia/Riyadh", label: "Riyadh (AST)" },
  { value: "Asia/Kolkata", label: "Mumbai / New Delhi (IST)" },
  { value: "Asia/Dhaka", label: "Dhaka (BST)" },
  { value: "Asia/Bangkok", label: "Bangkok (ICT)" },
  { value: "Asia/Singapore", label: "Singapore (SGT)" },
  { value: "Asia/Shanghai", label: "Beijing / Shanghai (CST)" },
  { value: "Asia/Tokyo", label: "Tokyo (JST)" },
  { value: "Asia/Seoul", label: "Seoul (KST)" },
  { value: "Australia/Sydney", label: "Sydney (AEST / AEDT)" },
  { value: "Australia/Melbourne", label: "Melbourne (AEST / AEDT)" },
  { value: "Pacific/Auckland", label: "Auckland (NZST / NZDT)" },
  // Africa
  { value: "Africa/Cairo", label: "Cairo (EET)" },
  { value: "Africa/Johannesburg", label: "Johannesburg (SAST)" },
  { value: "Africa/Lagos", label: "Lagos (WAT)" },
  { value: "Africa/Nairobi", label: "Nairobi (EAT)" },
];

const statusConfig: Record<string, { color: string; bg: string; icon: typeof CheckCircle2 }> = {
  Active:   { color: "text-emerald-600", bg: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  Disabled: { color: "text-slate-500",   bg: "bg-slate-100 text-slate-500 border-slate-200",       icon: XCircle },
};

const validStatuses = ["Active", "Disabled"];

// ── SSO Tab ───────────────────────────────────────────────────────────────────
function SsoTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const metadataUrl = `${origin}/sso/saml/metadata.xml`;
  const acsUrl = `${origin}/sso/saml/assertion.html`;

  const { data: cfg, isLoading } = useQuery<SsoConfig>({
    queryKey: ["sso-config"],
    queryFn: apiGetSsoConfig,
  });

  const [enabled, setEnabled] = useState(false);
  const [federationUrl, setFederationUrl] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (cfg) {
      setEnabled(!!cfg.enabled);
      setFederationUrl(cfg.federation_metadata_url ?? "");
    }
  }, [cfg]);

  const mutation = useMutation({
    mutationFn: apiSaveSsoConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sso-config"] });
      setSaveError(null);
      toast.success(t("organization.ssoSaved"));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
    onError: (err: any) => {
      const msg = err?.message ?? t("organization.ssoSaveError");
      setSaveError(msg);
      toast.error(msg);
    },
  });

  function copy(text: string) {
    navigator.clipboard.writeText(text).then(() => toast.success(t("organization.copied")));
  }

  if (isLoading) return <div className="py-8 text-center text-muted-foreground text-sm">{t("common.loading", "Loading…")}</div>;

  return (
    <div className="space-y-6 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            {t("organization.ssoTitle")}
          </CardTitle>
          <CardDescription>
            {t("organization.ssoDesc")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Enable toggle */}
          <div className="flex items-center justify-between rounded-lg border border-border p-4">
            <div>
              <p className="text-sm font-medium">{t("organization.enableSso")}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{t("organization.enableSsoHint")}</p>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} />
          </div>

          {/* Federation Metadata URL */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">{t("organization.federationMetadataUrl")}</Label>
            <Input
              className="h-9 text-sm font-mono"
              placeholder="https://login.microsoftonline.com/{tenant}/federationmetadata/2007-06/federationmetadata.xml"
              value={federationUrl}
              onChange={(e) => { setFederationUrl(e.target.value); setSaveError(null); }}
            />
            <p className="text-[11px] text-muted-foreground">
              {t("organization.federationMetadataHint")}
            </p>
          </div>

          {/* Parse error */}
          {saveError && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2">
              <p className="text-xs text-destructive">{saveError}</p>
            </div>
          )}

          {/* SP info — read-only for admin to copy into IdP */}
          <div className="space-y-3 rounded-lg bg-muted/40 border border-border p-4">
            <h3 className="text-sm font-semibold text-foreground/80">{t("organization.spConfigTitle")}</h3>
            <p className="text-xs text-muted-foreground">{t("organization.spConfigDesc")}</p>
            {[
              { label: t("organization.acsUrlLabel"), value: acsUrl },
              { label: t("organization.spMetadataLabel"), value: metadataUrl },
            ].map(({ label, value }) => (
              <div key={label} className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">{label}</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-background border border-border rounded px-2 py-1.5 break-all">{value}</code>
                  <Button variant="ghost" size="sm" className="h-7 px-2 shrink-0" onClick={() => copy(value)}>
                    {t("organization.copy")}
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <Button
            className="w-full"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate({ enabled, federation_metadata_url: federationUrl })}
          >
            {mutation.isPending ? t("organization.savingSso") : saved ? t("organization.savedSso") : t("organization.saveSso")}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

const Organization = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const isCLevel = user?.role?.subRole === "c_level";
  const isAdmin = user?.role?.mainRole === "admin";
  const isHrOrAdmin = user?.role?.mainRole === "hr" || user?.role?.mainRole === "admin";
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const p = searchParams.get("tab");
    return p === "teams" || p === "company" || p === "employees" || p === "departments" || p === "positions" ? p : "overview";
  });

  const [company, setCompany] = useState<CompanyDetails | null>(null);
  const [stats, setStats] = useState<OrgStats | null>(null);
  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const hasLoadedRef = useRef(false);
  const [companyForm, setCompanyForm] = useState<Partial<CompanyDetails>>({});
  const [savingCompany, setSavingCompany] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deptFilter, setDeptFilter] = useState("all");
  const [empPage, setEmpPage] = useState(1);

  // Auto-open add employee form when navigated from Quick Actions
  useEffect(() => {
    if (searchParams.get("action") === "create" && searchParams.get("tab") === "employees" && !isCLevel) {
      setShowAddForm(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sheet states
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeListItem | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [showAddDeptDialog, setShowAddDeptDialog] = useState(false);
  const [showImportEmployees, setShowImportEmployees] = useState(false);
  const [showImportDepts, setShowImportDepts] = useState(false);
  const [showDeleteDeptDialog, setShowDeleteDeptDialog] = useState(false);
  const [deptToDelete, setDeptToDelete] = useState<Department | null>(null);

  const loadData = useCallback(async () => {
    try {
      // Only show the full-page skeleton on the very first load. Background
      // refreshes (e.g. after a CSV import via onComplete) must NOT unmount the
      // page, or any open dialog would be torn down and lose its state.
      if (!hasLoadedRef.current) setLoading(true);
      const [c, s, e, d] = await Promise.all([
        apiGetCompanyDetails(),
        apiGetOrgStats(),
        apiGetEmployees(),
        apiGetDepartments(),
      ]);
      setCompany(c);
      // If no quick links saved yet, seed the form with the platform defaults so HR
      // sees them pre-populated and can edit/remove rather than starting from blank.
      const seeded: CompanyDetails = c.quickLinks?.length
        ? c
        : { ...c, quickLinks: DEFAULT_QUICK_LINKS_ORG };
      setCompanyForm(seeded);
      setStats(s);
      setEmployees(e);
      setDepartments(d);
      hasLoadedRef.current = true;
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSaveCompany = async () => {
    setSavingCompany(true);
    try {
      const updated = await apiUpdateCompanyDetails(companyForm);
      setCompany(updated);
      setCompanyForm(updated);
      toast({ title: t("organization.companySaved"), description: t("organization.companySavedDesc") });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
    setSavingCompany(false);
  };

  const [tempPasswordInfo, setTempPasswordInfo] = useState<{ name: string; email: string; password: string } | null>(null);

  const handleAddEmployee = async (data: CreateEmployeePayload) => {
    try {
      const { employmentDetails, positionId, ...coreData } = data;
      const result = await apiCreateEmployee(coreData);
      // Save employment details when provided; these include required fixed-term and part-time values.
      if (employmentDetails && Object.values(employmentDetails).some((v) => v != null && v !== false && v !== "")) {
        await apiUpdateEmploymentDetails(String(result.id), employmentDetails);
      }
      // Assign position if selected
      if (positionId != null) {
        try { await apiAssignEmployeePosition(String(result.id), positionId); } catch { /* non-blocking */ }
      }
      setShowAddForm(false);
      await loadData();
      if (result.tempPassword) {
        setTempPasswordInfo({ name: result.name, email: result.email, password: result.tempPassword });
      } else {
        toast({ title: t("organization.employeeAdded") });
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleStatusChange = async (empId: number, status: string) => {
    try {
      await apiChangeEmployeeStatus(empId, status);
      await loadData();
      toast({ title: t("organization.statusChanged"), description: t("organization.statusChangedDesc", { status }) });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleDeactivate = async (empId: number) => {
    try {
      await apiDeactivateEmployee(empId);
      await loadData();
      toast({ title: t("organization.employeeDeactivated") });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const filtered = useMemo(() => {
    setEmpPage(1);
    let list = employees;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((e) =>
        e.name.toLowerCase().includes(q) || e.email.toLowerCase().includes(q) ||
        e.jobTitle.toLowerCase().includes(q) || e.department.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== "all") list = list.filter((e) => e.status === statusFilter);
    if (deptFilter !== "all") list = list.filter((e) => e.department === deptFilter);
    return list;
  }, [employees, search, statusFilter, deptFilter]);

  const EMP_PAGE_SIZE = 15;
  const empTotalPages = Math.max(1, Math.ceil(filtered.length / EMP_PAGE_SIZE));
  const pagedEmployees = filtered.slice((empPage - 1) * EMP_PAGE_SIZE, empPage * EMP_PAGE_SIZE);

  const uniqueDepts = useMemo(() => [...new Set(employees.map((e) => e.department))].sort(), [employees]);

  if (loading) {
    return (
      <AppLayout>
        <div className="space-y-6 w-full">
          <PageHeaderSkeleton />
          <Skeleton className="h-10 w-full rounded-lg" />
          <StatCardsSkeleton count={3} />
          <CardGridSkeleton count={4} className="grid grid-cols-1 lg:grid-cols-2 gap-6" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6 w-full">
        {/* Header */}
        <div className="flex items-center justify-between animate-in fade-in slide-in-from-left-5 duration-500">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
              <Building2 className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{t("organization.title")}</h1>
              <p className="text-sm text-muted-foreground mt-1">{t("organization.subtitle")}</p>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100">
          <TabsList className="w-full justify-start bg-transparent h-auto p-0 border-b border-border/60 rounded-none gap-0 overflow-x-auto overflow-y-hidden">
            <TabsTrigger value="overview" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <LayoutDashboard className="h-3.5 w-3.5" />{t("organization.overviewTab")}
            </TabsTrigger>
            <TabsTrigger value="company" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <Building2 className="h-3.5 w-3.5" />{t("organization.companyTab")}
            </TabsTrigger>
            <TabsTrigger value="employees" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <Users className="h-3.5 w-3.5" />{t("organization.employeesTab")}
            </TabsTrigger>
            <TabsTrigger value="departments" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <Layers className="h-3.5 w-3.5" />{t("organization.departmentsTab")}
            </TabsTrigger>
            <TabsTrigger value="teams" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <Users2 className="h-3.5 w-3.5" />{t("organization.teamsTab")}
            </TabsTrigger>
            {!isCLevel && (
              <TabsTrigger value="positions" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
                <GraduationCap className="h-3.5 w-3.5" />{t("organization.positionsTab", "Positions")}
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="sso" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
                <ShieldCheck className="h-3.5 w-3.5" />SSO
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="overview" className="mt-6 space-y-6">
            {stats && (
              <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="grid grid-cols-2 sm:grid-cols-4 sm:divide-x divide-border/60 divide-y sm:divide-y-0">
                  <StatCard label={t("organization.totalEmployees")} value={stats.totalEmployees} icon={Users} bg="bg-blue-500/10" color="text-blue-600" />
                  <StatCard label={t("organization.activeEmployees")} value={stats.byStatus.find((s) => s.status === "Active")?.count || 0} icon={CheckCircle2} bg="bg-emerald-500/10" color="text-emerald-600" />
                  <StatCard label={t("common.disabled")} value={stats.byStatus.find((s) => s.status === "Disabled")?.count || 0} icon={UserMinus} bg="bg-slate-500/10" color="text-slate-600" />
                </div>
              </div>
            )}
            {stats && <OverviewCharts stats={stats} t={t} />}
          </TabsContent>

          <TabsContent value="company" className="mt-6">
            <CompanyForm
              form={companyForm}
              setForm={setCompanyForm}
              saving={savingCompany}
              onSave={handleSaveCompany}
              onLogoUploaded={(url) => setCompanyForm((f) => ({ ...f, logoUrl: url || null }))}
              readOnly={isCLevel}
              t={t}
            />
          </TabsContent>

          {/* Employees Tab */}
          <TabsContent value="employees" className="mt-6 space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("organization.searchEmployees")}
                  className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-3 text-sm" />
              </div>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="h-9 rounded-lg border border-input bg-background px-3 text-sm">
                <option value="all">{t("organization.allStatuses")}</option>
                {validStatuses.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}
                className="h-9 rounded-lg border border-input bg-background px-3 text-sm">
                <option value="all">{t("organization.allDepartments")}</option>
                {uniqueDepts.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              {!isCLevel && (
              <Button size="sm" variant="outline" onClick={() => setShowImportEmployees(true)} className="gap-1.5">
                <Upload className="h-4 w-4" />{t("bulkImport.importCsv", "Upload Excel")}
              </Button>
              )}
              {!isCLevel && (
              <Button size="sm" onClick={() => setShowAddForm(true)} className="gap-1.5">
                <UserPlus className="h-4 w-4" />{t("organization.addEmployee")}
              </Button>
              )}
            </div>

            {!isCLevel && (
              <BulkImportDialog
                open={showImportEmployees}
                onClose={() => setShowImportEmployees(false)}
                entity="employees"
                onComplete={loadData}
              />
            )}

            {!isCLevel && (
              <AddEmployeeSheet
                open={showAddForm}
                onClose={() => setShowAddForm(false)}
                onSubmit={handleAddEmployee}
                departments={departments.map((d) => d.name)}
                employees={employees}
                companyCountry={company?.country ?? null}
                t={t}
              />
            )}

            {/* Temp password dialog shown after employee creation */}
            <Dialog open={!!tempPasswordInfo} onOpenChange={(v) => { if (!v) setTempPasswordInfo(null); }}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <UserPlus className="h-5 w-5 text-primary" />
                    {t("organization.employeeCreatedTitle")}
                  </DialogTitle>
                  <DialogDescription>
                    {t("organization.employeeCreatedDesc", { name: tempPasswordInfo?.name ?? "" })}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-2">
                  <div className="rounded-lg border bg-muted/50 p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t("organization.fieldEmail")}</span>
                      <span className="font-medium">{tempPasswordInfo?.email}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">{t("organization.fieldTempPassword")}</span>
                      <span className="font-mono font-semibold tracking-wider text-base">{tempPasswordInfo?.password}</span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">{t("organization.firstLoginHint")}</p>
                </div>
                <DialogFooter>
                  <Button onClick={() => setTempPasswordInfo(null)}>{t("organization.done")}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <div className="text-xs text-muted-foreground">{filtered.length} {t("organization.employeesTab").toLowerCase()}</div>
            <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-border/60 bg-muted/30 hover:bg-muted/30">
                    <TableHead className="pl-4 font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("organization.colEmployee")}</TableHead>
                    <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("organization.colJobTitle")}</TableHead>
                    <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden md:table-cell">{t("organization.colDepartment")}</TableHead>
                    <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden lg:table-cell">{t("organization.colLocation")}</TableHead>
                    <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden lg:table-cell">{t("organization.colContract")}</TableHead>
                    <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("organization.status")}</TableHead>
                    <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden xl:table-cell">{t("organization.colStartDate")}</TableHead>
                    {!isCLevel && <TableHead className="w-10" />}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="py-12">
                        <div className="flex flex-col items-center gap-3 text-center">
                          <Sparkles className="h-8 w-8 text-muted-foreground/40" />
                          <p className="text-sm text-muted-foreground">{t("organization.noEmployeesFound")}</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    pagedEmployees.map((emp) => {
                      const sc = statusConfig[emp.status] || statusConfig.Active;
                      const StatusIcon = sc.icon;
                      return (
                        <TableRow
                          key={emp.id}
                          className={`border-b border-border/40 transition-colors${isCLevel ? "" : " cursor-pointer hover:bg-muted/40"}`}
                          onClick={() => !isCLevel && setSelectedEmployee(emp)}
                        >
                          <TableCell className="pl-4 py-3">
                            <div className="flex items-center gap-3">
                              <EmployeeAvatar
                                avatarUrl={emp.avatarUrl}
                                avatarColor={emp.avatarColor}
                                initials={emp.initials}
                                size="sm"
                                className="shrink-0"
                              />
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{emp.name}</p>
                                <p className="text-xs text-muted-foreground truncate">{emp.employeeId}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="py-3">
                            <span className="text-sm text-foreground">{emp.jobTitle || "—"}</span>
                          </TableCell>
                          <TableCell className="py-3 hidden md:table-cell">
                            <span className="text-sm text-muted-foreground">{emp.department || "—"}</span>
                          </TableCell>
                          <TableCell className="py-3 hidden lg:table-cell">
                            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                              <MapPin className="h-3.5 w-3.5 shrink-0" />
                              {emp.location || "—"}
                            </div>
                          </TableCell>
                          <TableCell className="py-3 hidden lg:table-cell">
                            <span className="text-sm text-muted-foreground">{getContractTypeLabel(emp.contractType, t) || "—"}</span>
                          </TableCell>
                          <TableCell className="py-3">
                            <Badge variant="outline" className={`text-[10px] border ${sc.bg}`}>
                              <StatusIcon className="h-3 w-3 mr-0.5" />{emp.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-3 hidden xl:table-cell">
                            <span className="text-sm text-muted-foreground">
                              {emp.startDate ? new Date(emp.startDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                            </span>
                          </TableCell>
                          {!isCLevel && (
                            <TableCell className="py-3 w-10">
                              <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
            {empTotalPages > 1 && (
              <div className="flex items-center justify-between pt-1">
                <span className="text-xs text-muted-foreground">
                  Page {empPage} of {empTotalPages}
                </span>
                <div className="flex items-center gap-1.5">
                  <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={empPage === 1} onClick={() => setEmpPage(empPage - 1)}>
                    Previous
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={empPage === empTotalPages} onClick={() => setEmpPage(empPage + 1)}>
                    Next
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Departments Tab */}
          <TabsContent value="departments" className="mt-6 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{t("organization.deptCount", { count: departments.length })}</p>
              {!isCLevel && (
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => setShowImportDepts(true)} className="gap-1.5">
                  <Upload className="h-4 w-4" />{t("bulkImport.importCsv", "Upload Excel")}
                </Button>
                <Button size="sm" onClick={() => setShowAddDeptDialog(true)} className="gap-1.5">
                  <Plus className="h-4 w-4" />{t("organization.addDepartment")}
                </Button>
              </div>
              )}
            </div>

            {!isCLevel && (
              <BulkImportDialog
                open={showImportDepts}
                onClose={() => setShowImportDepts(false)}
                entity="departments"
                onComplete={loadData}
              />
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {departments.map((dept, idx) => (
                <Card key={dept.id}
                  className={`relative overflow-hidden shadow-card transition-colors duration-200 animate-in fade-in slide-in-from-bottom-4 duration-500 group${isCLevel ? "" : " cursor-pointer hover:bg-muted/20"}`}
                  style={{ animationDelay: `${idx * 60}ms` }}
                  onClick={() => !isCLevel && setSelectedDepartment(dept)}
                >
                  <DecorativeIconPicker
                    value={dept.icon}
                    defaultIconKey={getDefaultDepartmentIconKey(dept.name)}
                    onSelect={async (key) => {
                      try {
                        const updated = await apiUpdateDepartment(dept.id, { icon: key });
                        setDepartments((prev) => prev.map((d) => d.id === dept.id ? { ...d, icon: updated.icon } : d));
                      } catch (err: any) {
                        toast({ title: "Error", description: err.message, variant: "destructive" });
                      }
                    }}
                  />
                  <CardContent className="relative z-10 p-4">
                    <div className="flex items-start justify-between mb-1.5">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors truncate">{dept.name}</h3>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span className="text-xs text-muted-foreground">
                            {dept.employeeCount} {t("organization.employeesTab").toLowerCase()}
                          </span>
                          {dept.headName && (
                            <>
                              <span className="text-muted-foreground/40 text-xs">·</span>
                              <Crown className="h-3 w-3 text-amber-500" />
                              <span className="text-xs text-muted-foreground">{dept.headName}</span>
                            </>
                          )}
                        </div>
                      </div>
                      {!isCLevel && <ChevronRight className="h-4 w-4 text-muted-foreground/50 shrink-0 mt-0.5" />}
                    </div>
                    {dept.description && <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{dept.description}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Teams Tab */}
          <TabsContent value="teams" className="mt-6">
            <TeamsTab />
          </TabsContent>
          {!isCLevel && (
            <TabsContent value="positions" className="mt-6">
              <PositionsTab t={t} />
            </TabsContent>
          )}
          {isAdmin && (
            <TabsContent value="sso" className="mt-6">
              <SsoTab />
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Employee Edit Sheet */}
      <EmployeeEditSheet
        employee={selectedEmployee}
        open={!!selectedEmployee}
        onClose={() => setSelectedEmployee(null)}
        onStatusChange={handleStatusChange}
        onDeactivate={handleDeactivate}
        isAdmin={isAdmin}
        t={t}
      />

      {/* Department Edit Sheet */}
      <DepartmentEditSheet
        department={selectedDepartment}
        open={!!selectedDepartment}
        onClose={() => setSelectedDepartment(null)}
        employees={employees}
        onSave={async (id, data, memberChanges) => {
          try {
            const newName = data.name ?? selectedDepartment?.name ?? "";
            await apiUpdateDepartment(id, data);
            if (memberChanges) {
              await Promise.all([
                ...memberChanges.toAdd.map((empId) => apiUpdateEmployee(empId, { department: newName })),
                ...memberChanges.toRemove.map((empId) => apiUpdateEmployee(empId, { department: "" })),
              ]);
            }
            setSelectedDepartment(null);
            await loadData();
            toast({ title: t("organization.departmentSaved") });
          } catch (err: any) {
            toast({ title: "Error", description: err.message, variant: "destructive" });
          }
        }}
        onDelete={(dept) => { setDeptToDelete(dept); setShowDeleteDeptDialog(true); }}
        t={t}
      />

      {/* Add Department Dialog */}
      <AddDepartmentDialog
        open={showAddDeptDialog}
        onClose={() => setShowAddDeptDialog(false)}
        employees={employees}
        onSubmit={async ({ memberIds, ...data }) => {
          try {
            await apiCreateDepartment(data);
            if (memberIds && memberIds.length > 0) {
              await Promise.all(memberIds.map((id) => apiUpdateEmployee(id, { department: data.name })));
            }
            setShowAddDeptDialog(false);
            await loadData();
            toast({ title: t("organization.departmentAdded") });
          } catch (err: any) {
            toast({ title: "Error", description: err.message, variant: "destructive" });
          }
        }}
        t={t}
      />

      {/* Delete Department Dialog */}
      <DeleteDepartmentDialog
        open={showDeleteDeptDialog}
        onClose={() => { setShowDeleteDeptDialog(false); setDeptToDelete(null); }}
        department={deptToDelete}
        departments={departments}
        onConfirm={async (deptId, reassignTo) => {
          try {
            await apiDeleteDepartment(deptId, reassignTo);
            setShowDeleteDeptDialog(false);
            setDeptToDelete(null);
            setSelectedDepartment(null);
            await loadData();
            toast({ title: t("organization.departmentDeleted") });
          } catch (err: any) {
            toast({ title: "Error", description: err.message, variant: "destructive" });
          }
        }}
        t={t}
      />
    </AppLayout>
  );
};

// ============================================================
// Sub-components
// ============================================================

function StatCard({ label, value, icon: Icon, bg, color }: { label: string; value: number; icon: any; bg: string; color: string }) {
  return (
    <div className="flex items-center gap-3.5 px-5 py-4">
      <div className={`h-9 w-9 rounded-xl ${bg} flex items-center justify-center shrink-0`}>
        <Icon className={`h-4 w-4 ${color}`} />
      </div>
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <p className="text-2xl font-bold tabular-nums text-foreground leading-none">{value}</p>
      </div>
    </div>
  );
}

function OverviewCharts({ stats, t }: { stats: OrgStats; t: any }) {
  const statusData = stats.byStatus.map((s) => ({ name: s.status, value: s.count }));
  const deptData = stats.byDepartment;
  const contractData = Array.from(
    stats.byContractType.reduce((acc, c) => {
      const name = getContractTypeLabel(c.contractType, t) || c.contractType;
      acc.set(name, (acc.get(name) ?? 0) + c.count);
      return acc;
    }, new Map<string, number>())
  ).map(([name, value]) => ({ name, value }));

  const tenureData = (stats.demographics?.byTenure ?? []).map((b) => ({
    name: t(`organization.demographics.tenureBuckets.${b.bucket}`),
    value: b.count,
  }));
  const genderData = (stats.demographics?.byGender ?? []).map((g) => ({
    name: t(`organization.demographics.genderLabels.${g.gender.toLowerCase()}`, g.gender),
    value: g.count,
  }));
  const absenceData = stats.absenceRateByDepartment ?? [];
  const currencies = new Set((stats.salaryByDepartment ?? []).map((s) => s.currency));
  const salaryData = (stats.salaryByDepartment ?? []).map((s) => ({
    department: currencies.size > 1 ? `${s.department} (${s.currency})` : s.department,
    avgSalary: s.avgSalary,
    currency: s.currency,
  }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="shadow-card animate-in fade-in slide-in-from-left-4 duration-500">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-muted-foreground" />{t("organization.byStatus")}</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={statusData} cx="50%" cy="50%" innerRadius={55} outerRadius={100} dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                animationDuration={1200} animationEasing="ease-out">
                {statusData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid hsl(var(--border))", backgroundColor: "hsl(var(--card))", fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card className="shadow-card animate-in fade-in slide-in-from-right-4 duration-500">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Building2 className="h-4 w-4 text-muted-foreground" />{t("organization.byDepartment")}</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={deptData} layout="vertical" barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis type="category" dataKey="department" width={110} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid hsl(var(--border))", backgroundColor: "hsl(var(--card))", fontSize: 12 }} />
              <Bar dataKey="count" radius={[0, 6, 6, 0]} animationDuration={1200}>
                {deptData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card className="shadow-card animate-in fade-in slide-in-from-left-4 duration-500 delay-150">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4 text-muted-foreground" />{t("organization.byContractType")}</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={contractData} cx="50%" cy="50%" innerRadius={55} outerRadius={100} dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                animationDuration={1200} animationEasing="ease-out">
                {contractData.map((_, i) => <Cell key={i} fill={PIE_COLORS[(i + 3) % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid hsl(var(--border))", backgroundColor: "hsl(var(--card))", fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card className="shadow-card animate-in fade-in slide-in-from-right-4 duration-500 delay-150">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4 text-muted-foreground" />{t("organization.recentHires")}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {stats.recentEmployees.map((emp) => {
            const sc = statusConfig[emp.status] || statusConfig.Active;
            return (
              <div key={emp.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                <EmployeeAvatar
                  avatarUrl={emp.avatarUrl}
                  avatarColor={emp.avatarColor}
                  initials={emp.initials}
                  size="sm"
                  className="shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{emp.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{emp.jobTitle} &bull; {emp.department}</p>
                </div>
                <Badge variant="outline" className={`text-[10px] border ${sc.bg}`}>{emp.status}</Badge>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card className="shadow-card animate-in fade-in slide-in-from-left-4 duration-500 delay-200">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Clock className="h-4 w-4 text-muted-foreground" />{t("organization.demographics.byTenure")}</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={tenureData} cx="50%" cy="50%" innerRadius={55} outerRadius={100} dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                animationDuration={1200} animationEasing="ease-out">
                {tenureData.map((_, i) => <Cell key={i} fill={PIE_COLORS[(i + 1) % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid hsl(var(--border))", backgroundColor: "hsl(var(--card))", fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card className="shadow-card animate-in fade-in slide-in-from-right-4 duration-500 delay-200">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4 text-muted-foreground" />{t("organization.demographics.byGender")}</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={genderData} cx="50%" cy="50%" innerRadius={55} outerRadius={100} dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                animationDuration={1200} animationEasing="ease-out">
                {genderData.map((_, i) => <Cell key={i} fill={PIE_COLORS[(i + 5) % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid hsl(var(--border))", backgroundColor: "hsl(var(--card))", fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card className="shadow-card animate-in fade-in slide-in-from-left-4 duration-500 delay-300">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-muted-foreground" />{t("organization.demographics.absenceRateByDepartment")}</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={absenceData} layout="vertical" barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
              <XAxis type="number" unit="%" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis type="category" dataKey="department" width={110} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <Tooltip
                formatter={(value: number) => [`${value}%`, t("organization.demographics.absenceRate")]}
                contentStyle={{ borderRadius: "8px", border: "1px solid hsl(var(--border))", backgroundColor: "hsl(var(--card))", fontSize: 12 }}
              />
              <Bar dataKey="absenceRate" radius={[0, 6, 6, 0]} animationDuration={1200} fill={PIE_COLORS[6]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card className="shadow-card animate-in fade-in slide-in-from-right-4 duration-500 delay-300">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Wallet className="h-4 w-4 text-muted-foreground" />{t("organization.demographics.salaryByDepartment")}</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={salaryData} layout="vertical" barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis type="category" dataKey="department" width={110} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <Tooltip
                formatter={(value: number, _name: string, item: any) => [`${value.toLocaleString()} ${item.payload.currency}`, t("organization.demographics.avgSalary")]}
                contentStyle={{ borderRadius: "8px", border: "1px solid hsl(var(--border))", backgroundColor: "hsl(var(--card))", fontSize: 12 }}
              />
              <Bar dataKey="avgSalary" radius={[0, 6, 6, 0]} animationDuration={1200}>
                {salaryData.map((_, i) => <Cell key={i} fill={PIE_COLORS[(i + 2) % PIE_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}

import { API_BASE as API_BASE_ORG } from "@/lib/api";

function CompanyForm({ form, setForm, saving, onSave, onLogoUploaded, readOnly = false, t }: {
  form: Partial<CompanyDetails>; setForm: (f: Partial<CompanyDetails>) => void;
  saving: boolean; onSave: () => void; onLogoUploaded: (url: string) => void;
  readOnly?: boolean; t: any;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const update = (key: keyof CompanyDetails, value: string) => setForm({ ...form, [key]: value });

  async function handleLogoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const url = await apiUploadCompanyLogo(file);
      onLogoUploaded(url);
      queryClient.invalidateQueries({ queryKey: ["company-logo", getTenantSlug()] });
      toast({ title: t("organization.logoUploaded") });
    } catch {
      toast({ title: t("organization.logoUploadError"), variant: "destructive" });
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  const inp = (key: keyof CompanyDetails, type = "text") => (
    <input
      type={type}
      value={(form[key] as string) || ""}
      onChange={(e) => !readOnly && update(key, e.target.value)}
      readOnly={readOnly}
      className={`h-9 w-full rounded-lg border border-input bg-background px-3 text-sm${readOnly ? " cursor-default select-text" : ""}`}
    />
  );

  const fld = (key: keyof CompanyDetails, label: string, Icon: typeof Building2, type = "text", colSpan = "") => (
    <div key={key} className={colSpan}>
      <label className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5">
        <Icon className="h-3 w-3" />{label}
      </label>
      {inp(key, type)}
    </div>
  );

  return (
    <Card className="shadow-card animate-in fade-in slide-in-from-bottom-4 duration-500">
      <CardContent className="pt-6 space-y-6">

        {/* ── Company Logo ─────────────────────────────── */}
        <div className="flex items-center gap-5 p-4 rounded-lg border border-border bg-muted/30">
          <div className="h-16 w-16 rounded-lg border border-border bg-background flex items-center justify-center overflow-hidden shrink-0">
            {form.logoUrl ? (
              <img
                src={form.logoUrl?.startsWith("http") ? form.logoUrl : `${API_BASE_ORG}${form.logoUrl}`}
                alt="Company logo"
                className="h-full w-full object-contain p-1"
              />
            ) : (
              <Building2 className="h-7 w-7 text-muted-foreground" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground mb-1">
              {t("organization.companyLogo")}
            </p>
            <p className="text-xs text-muted-foreground mb-3">
              {t("organization.logoHint")}
            </p>
            <input ref={fileInputRef} type="file" accept=".png,.jpg,.jpeg,.webp,.svg" className="hidden" onChange={handleLogoChange} />
            {!readOnly && (
              <>
                <Button type="button" variant="outline" size="sm" disabled={uploadingLogo} onClick={() => fileInputRef.current?.click()} className="gap-2">
                  {uploadingLogo ? (
                    <><span className="animate-spin h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full" /> {t("common.uploading", "Uploading…")}</>
                  ) : (
                    <>{t("organization.uploadLogo")}</>
                  )}
                </Button>
                {form.logoUrl && (
                  <Button type="button" variant="ghost" size="sm" className="ml-2 text-destructive hover:text-destructive"
                    onClick={() => { setForm({ ...form, logoUrl: null }); onLogoUploaded(""); }}>
                    {t("organization.removeLogo")}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Identity ─────────────────────────────────── */}
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            {t("organization.sectionIdentity")}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fld("companyName", t("organization.companyName"), Building2, "text", "md:col-span-2")}
          </div>
        </div>

        {/* ── Legal & Registration ──────────────────────── */}
        <div className="border-t pt-5">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            {t("organization.sectionLegal")}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fld("legalName", t("organization.legalName"), FileText)}
            {fld("registrationNumber", t("organization.registrationNumber"), Hash)}
            {fld("taxNumber", t("organization.taxNumber"), Hash)}
          </div>
        </div>

        {/* ── Location ─────────────────────────────────── */}
        <div className="border-t pt-5">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            {t("organization.sectionLocation")}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fld("address", t("organization.address"), MapPin, "text", "md:col-span-2")}
            {fld("city", t("organization.city"), MapPin)}
            {fld("postalCode", t("organization.postalCode"), Hash)}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5">
                <Globe className="h-3 w-3" />{t("organization.country")}
              </label>
              <select
                value={(form.country as string) || ""}
                onChange={(e) => !readOnly && update("country", e.target.value)}
                disabled={readOnly}
                className={`h-9 w-full rounded-lg border border-input bg-background px-3 text-sm${readOnly ? " cursor-default opacity-70" : ""}`}
              >
                <option value="">{`— ${t("organization.selectCountry")} —`}</option>
                {COUNTRIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* ── Contact ──────────────────────────────────── */}
        <div className="border-t pt-5">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            {t("organization.sectionContact")}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fld("phone", t("organization.phone"), Phone)}
            {fld("email", t("organization.email"), Mail, "email")}
            {fld("website", t("organization.website"), Globe)}
          </div>
        </div>

        {/* ── Profile ──────────────────────────────────── */}
        <div className="border-t pt-5">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            {t("organization.sectionProfile")}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fld("industry", t("organization.industry"), Briefcase)}
            {fld("foundedDate", t("organization.foundedDate"), Calendar, "date")}
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5">
                <FileText className="h-3 w-3" />{t("organization.description")}
              </label>
              <textarea value={(form.description as string) || ""} onChange={(e) => !readOnly && update("description", e.target.value)}
                readOnly={readOnly} rows={3}
                className={`w-full rounded-lg border border-input bg-background px-3 py-2 text-sm resize-none${readOnly ? " cursor-default" : ""}`} />
            </div>
          </div>
        </div>

        {/* ── HR Settings ──────────────────────────────── */}
        <div className="border-t pt-5">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            {t("organization.sectionHR")}
          </p>
          <div className="space-y-4">
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
              <label className="text-xs font-semibold text-primary mb-1.5 flex items-center gap-1.5">
                <Globe className="h-3 w-3" />{t("organization.timezone")}
              </label>
              <p className="text-xs text-muted-foreground mb-2">{t("organization.timezoneHint")}</p>
              <select
                value={(form.timezone as string) || "UTC"}
                onChange={(e) => !readOnly && setForm({ ...form, timezone: e.target.value })}
                disabled={readOnly}
                className={`h-9 w-full max-w-xs rounded-lg border border-input bg-background px-3 text-sm${readOnly ? " cursor-default" : ""}`}
              >
                {TIMEZONES.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* ── Quick Links ───────────────────────────────── */}
        {!readOnly && (
          <QuickLinksSection
            links={form.quickLinks ?? []}
            onChange={(links) => setForm({ ...form, quickLinks: links })}
          />
        )}

        {!readOnly && (
          <div className="flex justify-end border-t pt-5">
            <Button onClick={onSave} disabled={saving} className="gap-2">
              <Save className="h-4 w-4" />{saving ? t("common.saving") : t("organization.saveCompany")}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Quick Links constants ──────────────────────────────────────────────────────

const PLATFORM_ROUTES = [
  { label: "Leave Management", path: "/leave-management" },
  { label: "My Salary", path: "/my-salary" },
  { label: "Onboarding (Admin)", path: "/onboarding" },
  { label: "My Onboarding", path: "/my-onboarding" },
  { label: "Offboarding", path: "/offboarding" },
  { label: "Organization", path: "/organization" },
  { label: "Performance", path: "/performance" },
  { label: "Payroll (Admin)", path: "/payroll" },
  { label: "Documents", path: "/documents" },
  { label: "Corporate Calendar", path: "/corporate-calendar" },
  { label: "My Tasks", path: "/my-tasks" },
  { label: "Team Overview", path: "/team-overview" },
  { label: "My Surveys", path: "/my-surveys" },
  { label: "My Profile", path: "/my-profile" },
  { label: "Surveys (Admin)", path: "/surveys" },
  { label: "Workflows", path: "/workflows" },
];

const ICON_OPTIONS: { name: string; icon: React.ComponentType<any> }[] = [
  { name: "BookOpen", icon: BookOpen },
  { name: "Clock", icon: Clock },
  { name: "FileText", icon: FileText },
  { name: "Shield", icon: Shield },
  { name: "Globe", icon: Globe },
  { name: "UserCheck", icon: UserCheck },
  { name: "Users", icon: Users },
  { name: "Calendar", icon: Calendar },
  { name: "BarChart3", icon: BarChart3 },
  { name: "Briefcase", icon: Briefcase },
  { name: "Star", icon: Star },
  { name: "Home", icon: Home },
  { name: "Mail", icon: Mail },
  { name: "Award", icon: Award },
  { name: "Zap", icon: Zap },
  { name: "Link2", icon: Link2 },
];

const COLOR_OPTIONS = [
  { name: "blue", class: "bg-blue-500" },
  { name: "green", class: "bg-green-500" },
  { name: "orange", class: "bg-orange-500" },
  { name: "purple", class: "bg-purple-500" },
  { name: "red", class: "bg-red-500" },
  { name: "cyan", class: "bg-cyan-500" },
  { name: "amber", class: "bg-amber-500" },
  { name: "indigo", class: "bg-indigo-500" },
];

function resolveColor(name: string) {
  return COLOR_OPTIONS.find((c) => c.name === name)?.class ?? "bg-blue-500";
}

function resolveIcon(name: string) {
  return ICON_OPTIONS.find((i) => i.name === name)?.icon ?? Link2;
}

// ── Quick Links section ────────────────────────────────────────────────────────

function QuickLinksSection({
  links,
  onChange,
}: {
  links: QuickLink[];
  onChange: (links: QuickLink[]) => void;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLink, setEditingLink] = useState<QuickLink | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── dialog form state ──
  const [dlLabel, setDlLabel] = useState("");
  const [dlLinkType, setDlLinkType] = useState<"internal" | "url" | "file">("internal");
  const [dlHref, setDlHref] = useState("");
  const [dlIcon, setDlIcon] = useState("BookOpen");
  const [dlColor, setDlColor] = useState("blue");

  function openAdd() {
    setEditingLink(null);
    setDlLabel("");
    setDlLinkType("internal");
    setDlHref(PLATFORM_ROUTES[0].path);
    setDlIcon("BookOpen");
    setDlColor("blue");
    setDialogOpen(true);
  }

  function openEdit(link: QuickLink) {
    setEditingLink(link);
    setDlLabel(link.label);
    setDlLinkType(link.linkType);
    setDlHref(link.href);
    setDlIcon(link.icon);
    setDlColor(link.color);
    setDialogOpen(true);
  }

  function handleSave() {
    if (!dlLabel.trim()) return;
    if (!dlHref.trim()) return;
    if (editingLink) {
      onChange(links.map((l) => l.id === editingLink.id ? { ...l, label: dlLabel.trim(), linkType: dlLinkType, href: dlHref.trim(), icon: dlIcon, color: dlColor } : l));
    } else {
      const newLink: QuickLink = {
        id: `ql-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        label: dlLabel.trim(),
        linkType: dlLinkType,
        href: dlHref.trim(),
        icon: dlIcon,
        color: dlColor,
      };
      onChange([...links, newLink]);
    }
    setDialogOpen(false);
  }

  function handleDelete(id: string) {
    onChange(links.filter((l) => l.id !== id));
  }

  function moveUp(idx: number) {
    if (idx === 0) return;
    const arr = [...links];
    [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
    onChange(arr);
  }

  function moveDown(idx: number) {
    if (idx === links.length - 1) return;
    const arr = [...links];
    [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
    onChange(arr);
  }

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      const url = await apiUploadQuickLinkFile(file);
      setDlHref(url);
    } catch {
      toast({ title: t("organization.qlFileUploadFailed"), variant: "destructive" });
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  const IconComp = resolveIcon(dlIcon);

  return (
    <div className="border-t pt-5">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("organization.quickLinks")}</p>
        <Button type="button" variant="outline" size="sm" className="gap-1.5 h-8 text-xs" onClick={openAdd}>
          <Plus className="h-3.5 w-3.5" />{t("organization.addLink")}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        {t("organization.quickLinksDesc")}
      </p>

      {links.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border py-6 text-center">
          <p className="text-sm text-muted-foreground">{t("organization.noQuickLinks")}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {links.map((link, idx) => {
            const IconEl = resolveIcon(link.icon);
            const colorCls = resolveColor(link.color);
            return (
              <div key={link.id} className="flex items-center gap-3 rounded-lg border border-border bg-muted/20 px-3 py-2">
                <div className={`h-7 w-7 rounded-md ${colorCls} flex items-center justify-center shrink-0`}>
                  <IconEl className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{link.label}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {link.linkType === "internal" ? t("organization.linkTypeInternal") : link.linkType === "file" ? t("organization.linkTypeFile") : t("organization.linkTypeExternal")} · {link.href}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveUp(idx)} disabled={idx === 0} title="Move up">
                    <ChevronRight className="h-3.5 w-3.5 -rotate-90" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveDown(idx)} disabled={idx === links.length - 1} title="Move down">
                    <ChevronRight className="h-3.5 w-3.5 rotate-90" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(link)} title="Edit">
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDelete(link.id)} title="Delete">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingLink ? t("organization.qlEditTitle") : t("organization.qlAddTitle")}</DialogTitle>
            <DialogDescription>{t("organization.qlDialogDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Label */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{t("organization.qlLabel")}</label>
              <input
                type="text"
                value={dlLabel}
                onChange={(e) => setDlLabel(e.target.value)}
                placeholder={t("organization.qlLabelPlaceholder")}
                className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"
              />
            </div>

            {/* Link type */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{t("organization.qlLinkType")}</label>
              <div className="flex gap-2">
                {(["internal", "url", "file"] as const).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => { setDlLinkType(type); setDlHref(type === "internal" ? PLATFORM_ROUTES[0].path : ""); }}
                    className={`flex-1 py-1.5 px-3 rounded-lg text-xs border transition-colors ${dlLinkType === type ? "border-primary bg-primary/10 text-primary font-medium" : "border-border text-muted-foreground hover:border-muted-foreground"}`}
                  >
                    {type === "internal" ? t("organization.qlPlatformPage") : type === "url" ? t("organization.qlExternalUrl") : t("organization.qlUploadFile")}
                  </button>
                ))}
              </div>
            </div>

            {/* Destination */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                {dlLinkType === "internal" ? t("organization.qlPlatformPage") : dlLinkType === "url" ? "URL" : t("organization.linkTypeFile")}
              </label>
              {dlLinkType === "internal" ? (
                <select
                  value={dlHref}
                  onChange={(e) => setDlHref(e.target.value)}
                  className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"
                >
                  {PLATFORM_ROUTES.map((r) => (
                    <option key={r.path} value={r.path}>{r.label}</option>
                  ))}
                </select>
              ) : dlLinkType === "url" ? (
                <input
                  type="url"
                  value={dlHref}
                  onChange={(e) => setDlHref(e.target.value)}
                  placeholder="https://example.com"
                  className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"
                />
              ) : (
                <div className="space-y-2">
                  <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} />
                  <Button type="button" variant="outline" size="sm" disabled={uploadingFile} onClick={() => fileInputRef.current?.click()} className="gap-2">
                    {uploadingFile ? (
                      <><span className="animate-spin h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full" /> {t("organization.qlUploading")}</>
                    ) : t("organization.qlChooseFile")}
                  </Button>
                  {dlHref && (
                    <p className="text-xs text-muted-foreground truncate">
                      {t("organization.qlUploaded")} {dlHref.split("/").pop()}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Icon */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{t("organization.qlIcon")}</label>
              <div className="flex flex-wrap gap-2">
                {ICON_OPTIONS.map(({ name, icon: IconItem }) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => setDlIcon(name)}
                    className={`h-8 w-8 rounded-lg border flex items-center justify-center transition-colors ${dlIcon === name ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-muted-foreground"}`}
                    title={name}
                  >
                    <IconItem className="h-4 w-4" />
                  </button>
                ))}
              </div>
            </div>

            {/* Color */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{t("organization.qlColour")}</label>
              <div className="flex flex-wrap gap-2">
                {COLOR_OPTIONS.map(({ name, class: cls }) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => setDlColor(name)}
                    className={`h-7 w-7 rounded-md ${cls} transition-transform ${dlColor === name ? "ring-2 ring-offset-2 ring-primary scale-110" : "opacity-70 hover:opacity-100"}`}
                    title={name}
                  />
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2">
              <div className={`h-7 w-7 rounded-md ${resolveColor(dlColor)} flex items-center justify-center shrink-0`}>
                <IconComp className="h-4 w-4 text-white" />
              </div>
              <span className="text-sm font-medium">{dlLabel || t("organization.qlLinkLabelFallback")}</span>
              <span className="text-xs text-muted-foreground ml-auto">{dlLinkType === "internal" ? t("organization.qlPlatformPageShort") : dlLinkType === "file" ? t("organization.linkTypeFile") : t("organization.qlExternalShort")}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleSave} disabled={!dlLabel.trim() || !dlHref.trim()}>
              {editingLink ? t("organization.qlSave") : t("organization.qlAdd")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================
// Positions Tab
// ============================================================

function PositionsTab({ t }: { t: any }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [newPosTitle, setNewPosTitle] = useState("");
  const [newPosDesc, setNewPosDesc] = useState("");
  const [addingPos, setAddingPos] = useState(false);
  const [showImportPos, setShowImportPos] = useState(false);
  const [openDetailPosId, setOpenDetailPosId] = useState<number | null>(null);
  const [dlgTitle, setDlgTitle] = useState("");
  const [dlgDesc, setDlgDesc] = useState("");
  const [dlgCertInput, setDlgCertInput] = useState("");
  const [dlgTrainInput, setDlgTrainInput] = useState("");
  const [newCertTypeName, setNewCertTypeName] = useState("");
  const [newCertTypeDesc, setNewCertTypeDesc] = useState("");
  const [addingCertType, setAddingCertType] = useState(false);
  const [newTrainTypeName, setNewTrainTypeName] = useState("");
  const [newTrainTypeDesc, setNewTrainTypeDesc] = useState("");
  const [newTrainTypeRecurring, setNewTrainTypeRecurring] = useState(false);
  const [newTrainTypeMonths, setNewTrainTypeMonths] = useState("");
  const [addingTrainType, setAddingTrainType] = useState(false);

  const { data: positions = [], isLoading } = useQuery<Position[]>({
    queryKey: ["positions"],
    queryFn: apiGetPositions,
  });

  const { data: certTypes = [] } = useQuery<CertificateType[]>({
    queryKey: ["certificate-types"],
    queryFn: apiGetCertificateTypes,
    staleTime: 5 * 60_000,
  });

  const { data: trainingTypes = [] } = useQuery<TrainingType[]>({
    queryKey: ["training-types"],
    queryFn: apiGetTrainingTypes,
    staleTime: 5 * 60_000,
  });

  const createCertTypeMutation = useMutation({
    mutationFn: (data: { name: string; description?: string }) => apiCreateCertificateType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["certificate-types"] });
      setNewCertTypeName(""); setNewCertTypeDesc(""); setAddingCertType(false);
      toast({ title: t("certifications.certTypes.created", "Certificate type added") });
    },
    onError: () => toast({ title: t("common.error", "Error"), variant: "destructive" }),
  });

  const deleteCertTypeMutation = useMutation({
    mutationFn: (id: number) => apiDeleteCertificateType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["certificate-types"] });
    },
    onError: () => toast({ title: t("common.error", "Error"), variant: "destructive" }),
  });

  const createPosMutation = useMutation({
    mutationFn: (data: { title: string; description?: string }) => apiCreatePosition(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["positions"] });
      setNewPosTitle(""); setNewPosDesc(""); setAddingPos(false);
      toast({ title: t("certifications.positions.created", "Position created") });
    },
    onError: () => toast({ title: t("common.error", "Error"), variant: "destructive" }),
  });

  const updatePosMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<{ title: string; description: string }> }) =>
      apiUpdatePosition(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["positions"] });
      toast({ title: t("certifications.positions.updated", "Position updated") });
    },
    onError: () => toast({ title: t("common.error", "Error"), variant: "destructive" }),
  });

  const deletePosMutation = useMutation({
    mutationFn: (id: number) => apiDeletePosition(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["positions"] });
      queryClient.invalidateQueries({ queryKey: ["cert-overview"] });
      toast({ title: t("certifications.positions.deleted", "Position deleted") });
    },
    onError: () => toast({ title: t("common.error", "Error"), variant: "destructive" }),
  });

  const addReqMutation = useMutation({
    mutationFn: ({ posId, name }: { posId: number; name: string }) => apiAddPositionRequirement(posId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["positions"] });
      setDlgCertInput("");
    },
    onError: () => toast({ title: t("common.error", "Error"), variant: "destructive" }),
  });

  const removeReqMutation = useMutation({
    mutationFn: ({ posId, reqId }: { posId: number; reqId: number }) => apiRemovePositionRequirement(posId, reqId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["positions"] }),
    onError: () => toast({ title: t("common.error", "Error"), variant: "destructive" }),
  });

  const createTrainTypeMutation = useMutation({
    mutationFn: (data: { name: string; description?: string; isRecurring: boolean; recurrenceMonths?: number }) =>
      apiCreateTrainingType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["training-types"] });
      setNewTrainTypeName(""); setNewTrainTypeDesc(""); setNewTrainTypeRecurring(false);
      setNewTrainTypeMonths(""); setAddingTrainType(false);
      toast({ title: t("certifications.trainings.trainingTypes.created", "Training type added") });
    },
    onError: () => toast({ title: t("common.error", "Error"), variant: "destructive" }),
  });

  const deleteTrainTypeMutation = useMutation({
    mutationFn: (id: number) => apiDeleteTrainingType(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["training-types"] }),
    onError: () => toast({ title: t("common.error", "Error"), variant: "destructive" }),
  });

  const addTrainReqMutation = useMutation({
    mutationFn: ({ posId, name }: { posId: number; name: string }) =>
      apiAddPositionTrainingRequirement(posId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["positions"] });
      setDlgTrainInput("");
    },
    onError: () => toast({ title: t("common.error", "Error"), variant: "destructive" }),
  });

  const removeTrainReqMutation = useMutation({
    mutationFn: ({ posId, reqId }: { posId: number; reqId: number }) =>
      apiRemovePositionTrainingRequirement(posId, reqId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["positions"] }),
    onError: () => toast({ title: t("common.error", "Error"), variant: "destructive" }),
  });

  useEffect(() => {
    if (openDetailPosId != null) {
      const pos = positions.find((p) => p.id === openDetailPosId);
      if (pos) {
        setDlgTitle(pos.title);
        setDlgDesc(pos.description ?? "");
        setDlgCertInput("");
        setDlgTrainInput("");
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDetailPosId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading...
      </div>
    );
  }

  const openDetailPos = openDetailPosId != null ? (positions.find((p) => p.id === openDetailPosId) ?? null) : null;
  const isDirty = openDetailPos && (dlgTitle !== openDetailPos.title || dlgDesc !== (openDetailPos.description ?? ""));

  function closeDialog() {
    setOpenDetailPosId(null);
    setDlgCertInput("");
    setDlgTrainInput("");
  }

  function addCert(posId: number, name: string, existing: { certificationName: string }[]) {
    const n = name.trim();
    if (!n) return;
    if (existing.some((r) => r.certificationName.toLowerCase() === n.toLowerCase())) {
      toast({ title: t("certifications.positions.certAlreadyAdded", "Already added"), variant: "destructive" });
      return;
    }
    addReqMutation.mutate({ posId, name: n });
  }

  function addTrain(posId: number, name: string, existing: { trainingName: string }[]) {
    const n = name.trim();
    if (!n) return;
    if (existing.some((r) => r.trainingName.toLowerCase() === n.toLowerCase())) {
      toast({ title: t("certifications.positions.trainAlreadyAdded", "Already added"), variant: "destructive" });
      return;
    }
    addTrainReqMutation.mutate({ posId, name: n });
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">{t("certifications.positions.title", "Positions")}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t("organization.positionsDesc", "Define positions and assign required certifications and trainings. Click a position to manage its requirements.")}
          </p>
        </div>
        {!addingPos && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowImportPos(true)} className="gap-1.5">
              <Upload className="h-4 w-4" /> {t("bulkImport.importCsv", "Upload Excel")}
            </Button>
            <Button size="sm" onClick={() => setAddingPos(true)} className="gap-1.5">
              <Plus className="h-4 w-4" /> {t("certifications.positions.addPosition", "Add Position")}
            </Button>
          </div>
        )}
      </div>

      <BulkImportDialog
        open={showImportPos}
        onClose={() => setShowImportPos(false)}
        entity="positions"
        onComplete={() => queryClient.invalidateQueries({ queryKey: ["positions"] })}
      />

      {/* Add position inline form */}
      {addingPos && (
        <Card className="border-dashed">
          <CardContent className="pt-4 space-y-3">
            <Input
              autoFocus
              placeholder={t("certifications.positions.titlePlaceholder", "Position title e.g. Software Engineer")}
              value={newPosTitle}
              onChange={(e) => setNewPosTitle(e.target.value)}
              className="h-9"
              onKeyDown={(e) => {
                if (e.key === "Enter" && newPosTitle.trim()) createPosMutation.mutate({ title: newPosTitle.trim(), description: newPosDesc.trim() || undefined });
                if (e.key === "Escape") { setAddingPos(false); setNewPosTitle(""); setNewPosDesc(""); }
              }}
            />
            <Input
              placeholder={t("certifications.positions.descPlaceholder", "Description (optional)")}
              value={newPosDesc}
              onChange={(e) => setNewPosDesc(e.target.value)}
              className="h-9"
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={!newPosTitle.trim() || createPosMutation.isPending}
                onClick={() => createPosMutation.mutate({ title: newPosTitle.trim(), description: newPosDesc.trim() || undefined })}
              >
                {createPosMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                {t("common.save", "Save")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setAddingPos(false); setNewPosTitle(""); setNewPosDesc(""); }}>
                {t("common.cancel", "Cancel")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {positions.length === 0 && !addingPos && (
        <div className="flex flex-col items-center justify-center py-14 gap-2 text-muted-foreground">
          <Briefcase className="h-8 w-8 opacity-40" />
          <p className="text-sm">{t("certifications.positions.noPositions", "No positions defined yet")}</p>
          <p className="text-xs text-center max-w-xs opacity-70">{t("organization.positionsEmptyHint", "Add your first position, then click it to assign required certifications and trainings.")}</p>
        </div>
      )}

      {/* Position cards — clean, click to manage */}
      {positions.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {positions.map((pos) => (
            <div
              key={pos.id}
              onClick={() => setOpenDetailPosId(pos.id)}
              className="group bg-card border border-border rounded-xl p-4 shadow-sm cursor-pointer hover:border-primary/40 hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm truncate group-hover:text-primary transition-colors">{pos.title}</p>
                  {pos.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{pos.description}</p>}
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground/40 shrink-0 mt-0.5 group-hover:text-primary/60 transition-colors" />
              </div>
              <div className="flex items-center gap-3 mt-3 pt-2.5 border-t border-border/50">
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Award className="h-3 w-3 text-violet-500" />
                  {pos.requiredCertifications.length} {pos.requiredCertifications.length === 1 ? "cert" : "certs"}
                </span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <BookOpen className="h-3 w-3 text-blue-500" />
                  {(pos.requiredTrainings ?? []).length} {(pos.requiredTrainings ?? []).length === 1 ? "training" : "trainings"}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Master Lists — side by side */}
      <div className="border-t border-border/60 pt-5 grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* Certificate Master List */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Award className="h-4 w-4 text-violet-500" />
              <span className="text-sm font-semibold">{t("certifications.certTypes.title", "Certificate Master List")}</span>
            </div>
            {!addingCertType && (
              <Button size="sm" variant="outline" onClick={() => setAddingCertType(true)} className="gap-1 h-7 text-xs px-2.5">
                <Plus className="h-3 w-3" /> {t("certifications.certTypes.add", "Add")}
              </Button>
            )}
          </div>
          {addingCertType && (
            <div className="flex gap-2 p-3 rounded-lg border border-dashed bg-muted/30">
              <Input
                autoFocus
                placeholder={t("certifications.certTypes.namePlaceholder", "e.g. First Aid")}
                value={newCertTypeName}
                onChange={(e) => setNewCertTypeName(e.target.value)}
                className="h-8 text-sm flex-1"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newCertTypeName.trim()) createCertTypeMutation.mutate({ name: newCertTypeName.trim(), description: newCertTypeDesc.trim() || undefined });
                  if (e.key === "Escape") { setAddingCertType(false); setNewCertTypeName(""); setNewCertTypeDesc(""); }
                }}
              />
              <Button size="sm" className="h-8 text-xs shrink-0"
                disabled={!newCertTypeName.trim() || createCertTypeMutation.isPending}
                onClick={() => createCertTypeMutation.mutate({ name: newCertTypeName.trim(), description: newCertTypeDesc.trim() || undefined })}>
                {createCertTypeMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : t("common.save", "Save")}
              </Button>
              <Button size="sm" variant="ghost" className="h-8 text-xs shrink-0" onClick={() => { setAddingCertType(false); setNewCertTypeName(""); setNewCertTypeDesc(""); }}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
          {certTypes.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">{t("certifications.certTypes.empty", "No certificates yet.")}</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {certTypes.map((ct) => (
                <span key={ct.id} className="flex items-center gap-1.5 bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800 rounded-full px-2.5 py-1 text-xs font-medium text-violet-800 dark:text-violet-300" title={ct.description ?? undefined}>
                  {ct.name}
                  <button
                    className="text-violet-400 hover:text-destructive transition-colors"
                    onClick={() => { if (confirm(t("certifications.certTypes.deleteConfirm", "Remove from master list?"))) deleteCertTypeMutation.mutate(ct.id); }}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Training Master List */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-blue-500" />
              <span className="text-sm font-semibold">{t("certifications.trainings.trainingTypes.title", "Training Master List")}</span>
            </div>
            {!addingTrainType && (
              <Button size="sm" variant="outline" onClick={() => setAddingTrainType(true)} className="gap-1 h-7 text-xs px-2.5">
                <Plus className="h-3 w-3" /> {t("certifications.trainings.trainingTypes.add", "Add")}
              </Button>
            )}
          </div>
          {addingTrainType && (
            <div className="flex flex-col gap-2 p-3 rounded-lg border border-dashed bg-muted/30">
              <div className="flex gap-2">
                <Input
                  autoFocus
                  placeholder={t("certifications.trainings.trainingTypes.namePlaceholder", "e.g. Fire Safety")}
                  value={newTrainTypeName}
                  onChange={(e) => setNewTrainTypeName(e.target.value)}
                  className="h-8 text-sm flex-1"
                />
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none">
                  <input type="checkbox" checked={newTrainTypeRecurring} onChange={(e) => setNewTrainTypeRecurring(e.target.checked)} className="h-3.5 w-3.5" />
                  {t("certifications.trainings.trainingTypes.recurring", "Recurring")}
                </label>
                {newTrainTypeRecurring && (
                  <div className="flex items-center gap-1.5">
                    <Input type="number" min="1" placeholder="12" value={newTrainTypeMonths} onChange={(e) => setNewTrainTypeMonths(e.target.value)} className="h-7 text-xs w-16" />
                    <span className="text-xs text-muted-foreground">{t("certifications.trainings.trainingTypes.months", "months")}</span>
                  </div>
                )}
                <div className="ml-auto flex gap-1.5">
                  <Button size="sm" className="h-7 text-xs"
                    disabled={!newTrainTypeName.trim() || createTrainTypeMutation.isPending}
                    onClick={() => createTrainTypeMutation.mutate({ name: newTrainTypeName.trim(), description: newTrainTypeDesc.trim() || undefined, isRecurring: newTrainTypeRecurring, recurrenceMonths: newTrainTypeRecurring && newTrainTypeMonths ? Number(newTrainTypeMonths) : undefined })}>
                    {createTrainTypeMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : t("common.save", "Save")}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => { setAddingTrainType(false); setNewTrainTypeName(""); setNewTrainTypeDesc(""); setNewTrainTypeRecurring(false); setNewTrainTypeMonths(""); }}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          )}
          {trainingTypes.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">{t("certifications.trainings.trainingTypes.empty", "No trainings yet.")}</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {trainingTypes.map((tt) => (
                <span key={tt.id} className="flex items-center gap-1.5 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-full px-2.5 py-1 text-xs font-medium text-blue-800 dark:text-blue-300" title={tt.description ?? undefined}>
                  {tt.name}
                  {tt.isRecurring && tt.recurrenceMonths && (
                    <span className="opacity-60">· {tt.recurrenceMonths}m</span>
                  )}
                  <button
                    className="text-blue-400 hover:text-destructive transition-colors"
                    onClick={() => { if (confirm(t("certifications.certTypes.deleteConfirm", "Remove from master list?"))) deleteTrainTypeMutation.mutate(tt.id); }}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Position detail dialog ── */}
      <Dialog open={!!openDetailPos} onOpenChange={(v) => { if (!v) closeDialog(); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center shadow-md shrink-0">
                <Briefcase className="h-5 w-5 text-white" />
              </div>
              <div>
                <DialogTitle>{openDetailPos?.title ?? ""}</DialogTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {(openDetailPos?.requiredCertifications.length ?? 0)} certs · {(openDetailPos?.requiredTrainings ?? []).length} trainings
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 flex overflow-hidden min-h-0">
            {/* Left: edit title/desc + delete */}
            <div className="flex flex-col w-64 shrink-0 border-r border-border overflow-y-auto px-5 py-5 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Title</label>
                <Input value={dlgTitle} onChange={(e) => setDlgTitle(e.target.value)} className="h-9 text-sm" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Description</label>
                <textarea
                  value={dlgDesc}
                  onChange={(e) => setDlgDesc(e.target.value)}
                  placeholder="Optional description…"
                  rows={3}
                  className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              {isDirty && (
                <Button
                  size="sm"
                  className="gap-1.5"
                  disabled={!dlgTitle.trim() || updatePosMutation.isPending}
                  onClick={() => openDetailPos && updatePosMutation.mutate({ id: openDetailPos.id, data: { title: dlgTitle.trim(), description: dlgDesc.trim() || undefined } })}
                >
                  {updatePosMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                  {t("common.save", "Save")}
                </Button>
              )}
              <div className="mt-auto pt-4 border-t">
                <button
                  onClick={() => { if (openDetailPos && confirm(t("certifications.positions.deleteConfirm", "Delete this position?"))) { deletePosMutation.mutate(openDetailPos.id); closeDialog(); } }}
                  className="text-xs text-destructive hover:text-destructive/80 font-medium flex items-center gap-1.5 transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete position
                </button>
              </div>
            </div>

            {/* Right: requirements */}
            {openDetailPos && (
              <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">

                {/* Certifications */}
                <div className="space-y-2.5">
                  <div className="flex items-center gap-1.5">
                    <Award className="h-3.5 w-3.5 text-violet-500" />
                    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Required Certifications</span>
                  </div>
                  {openDetailPos.requiredCertifications.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">None added yet</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {openDetailPos.requiredCertifications.map((req) => (
                        <span key={req.id} className="flex items-center gap-1 bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800 rounded-full px-2.5 py-0.5 text-xs font-medium text-violet-800 dark:text-violet-300">
                          {req.certificationName}
                          <button className="ml-0.5 text-violet-400 hover:text-destructive transition-colors" onClick={() => removeReqMutation.mutate({ posId: openDetailPos.id, reqId: req.id })}>
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-1.5">
                    <input
                      type="text"
                      list={`cert-suggestions-${openDetailPos.id}`}
                      value={dlgCertInput}
                      onChange={(e) => setDlgCertInput(e.target.value)}
                      placeholder="Add certification…"
                      className="flex-1 h-8 rounded-lg border border-input bg-background px-3 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                      onKeyDown={(e) => { if (e.key === "Enter") addCert(openDetailPos.id, dlgCertInput, openDetailPos.requiredCertifications); }}
                    />
                    <datalist id={`cert-suggestions-${openDetailPos.id}`}>
                      {certTypes.filter((ct) => !openDetailPos.requiredCertifications.some((r) => r.certificationName.toLowerCase() === ct.name.toLowerCase())).map((ct) => (
                        <option key={ct.id} value={ct.name} />
                      ))}
                    </datalist>
                    <button
                      disabled={!dlgCertInput.trim() || addReqMutation.isPending}
                      onClick={() => addCert(openDetailPos.id, dlgCertInput, openDetailPos.requiredCertifications)}
                      className="h-8 w-8 rounded-lg border border-input flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/30 disabled:opacity-40 transition-colors"
                    >
                      {addReqMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                </div>

                <div className="border-t border-border/60" />

                {/* Trainings */}
                <div className="space-y-2.5">
                  <div className="flex items-center gap-1.5">
                    <BookOpen className="h-3.5 w-3.5 text-blue-500" />
                    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Required Trainings</span>
                  </div>
                  {(openDetailPos.requiredTrainings ?? []).length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">None added yet</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {(openDetailPos.requiredTrainings ?? []).map((req) => (
                        <span key={req.id} className="flex items-center gap-1 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-full px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:text-blue-300">
                          {req.trainingName}
                          <button className="ml-0.5 text-blue-400 hover:text-destructive transition-colors" onClick={() => removeTrainReqMutation.mutate({ posId: openDetailPos.id, reqId: req.id })}>
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-1.5">
                    <input
                      type="text"
                      list={`train-suggestions-${openDetailPos.id}`}
                      value={dlgTrainInput}
                      onChange={(e) => setDlgTrainInput(e.target.value)}
                      placeholder="Add training…"
                      className="flex-1 h-8 rounded-lg border border-input bg-background px-3 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                      onKeyDown={(e) => { if (e.key === "Enter") addTrain(openDetailPos.id, dlgTrainInput, openDetailPos.requiredTrainings ?? []); }}
                    />
                    <datalist id={`train-suggestions-${openDetailPos.id}`}>
                      {trainingTypes.filter((tt) => !(openDetailPos.requiredTrainings ?? []).some((r) => r.trainingName.toLowerCase() === tt.name.toLowerCase())).map((tt) => (
                        <option key={tt.id} value={tt.name} />
                      ))}
                    </datalist>
                    <button
                      disabled={!dlgTrainInput.trim() || addTrainReqMutation.isPending}
                      onClick={() => addTrain(openDetailPos.id, dlgTrainInput, openDetailPos.requiredTrainings ?? [])}
                      className="h-8 w-8 rounded-lg border border-input flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/30 disabled:opacity-40 transition-colors"
                    >
                      {addTrainReqMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={closeDialog}>{t("common.close", "Close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AddEmployeeSheet({ open, onClose, onSubmit, departments, employees, companyCountry, t }: {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateEmployeePayload) => Promise<void>;
  departments: string[];
  employees: EmployeeListItem[];
  companyCountry?: string | null;
  t: any;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState<CreateEmployeePayload>({
    name: "", email: "", jobTitle: "", department: "",
    contractType: DEFAULT_EMPLOYMENT_CONTRACT_TYPE, startDate: new Date().toISOString().slice(0, 10),
    reportsToId: null, mainRole: "user", subRole: "employee",
  });
  const { data: addPositions = [] } = useQuery<Position[]>({
    queryKey: ["positions"],
    queryFn: apiGetPositions,
    staleTime: 5 * 60_000,
  });
  const update = (key: string, value: any) => setForm((prev) => ({ ...prev, [key]: value }));

  const defaultEmpDetails: Partial<EmploymentDetails> = {
    contractEndDate: null, workArrangement: null, careerLevel: null, costCenter: null,
    benefitsEligible: false, probationEndDate: null, probationPeriodMonths: null,
    noticePeriodDays: null, hoursPerWeek: null, ftePercentage: null,
    partTimeHoursPerDay: null, partTimeIncludesSaturday: false, overtimeEligible: false,
    contractorCompany: null, scopeOfWork: null, billingCycle: null, ndaSigned: false,
    timeTrackingRequired: false, internshipType: null, mentorId: null,
    schoolUniversity: null, academicProgram: null, expectedGraduationDate: null,
    isConversionEligible: false,
  };
  const [empDetails, setEmpDetails] = useState<Partial<EmploymentDetails>>(defaultEmpDetails);
  const updateEmp = (key: keyof EmploymentDetails, value: any) =>
    setEmpDetails((prev) => ({ ...prev, [key]: value }));
  const selectedContractType = form.contractType || DEFAULT_EMPLOYMENT_CONTRACT_TYPE;
  const updateContractType = (value: EmploymentContractTypeValue) => {
    update("contractType", value);
    setEmpDetails((prev) => ({
      ...prev,
      contractEndDate: isFixedTermContractType(value) ? prev.contractEndDate : null,
      partTimeHoursPerDay: isPartTimeContractType(value) ? prev.partTimeHoursPerDay : null,
      partTimeIncludesSaturday: isPartTimeContractType(value) ? prev.partTimeIncludesSaturday : false,
      hoursPerWeek: isPartTimeContractType(value)
        ? calculatePartTimeWeeklyHours(prev.partTimeHoursPerDay, prev.partTimeIncludesSaturday)
        : prev.hoursPerWeek,
    }));
  };
  const isFixedTermContract = isFixedTermContractType(selectedContractType);
  const isPartTimeContract = isPartTimeContractType(selectedContractType);
  const fixedTermEndDateMissing = isFixedTermContract && !empDetails.contractEndDate;
  const partTimeHoursPerDayError = isPartTimeContract
    ? getPartTimeHoursPerDayError(empDetails.partTimeHoursPerDay, t)
    : "";
  const partTimeDetailsInvalid = isPartTimeContract && !!partTimeHoursPerDayError;
  const partTimeWeeklyHours = isPartTimeContract
    ? calculatePartTimeWeeklyHours(empDetails.partTimeHoursPerDay, empDetails.partTimeIncludesSaturday)
    : null;
  const buildEmploymentDetailsPayload = (): Partial<EmploymentDetails> => ({
    ...empDetails,
    contractEndDate: isFixedTermContract ? empDetails.contractEndDate : null,
    partTimeHoursPerDay: isPartTimeContract ? Number(empDetails.partTimeHoursPerDay) : null,
    partTimeIncludesSaturday: isPartTimeContract ? !!empDetails.partTimeIncludesSaturday : false,
    hoursPerWeek: isPartTimeContract ? partTimeWeeklyHours : empDetails.hoursPerWeek ?? null,
  });
  // Reset form when sheet opens
  useEffect(() => {
    if (open) {
      setForm({
        name: "", email: "", jobTitle: "", department: departments[0] || "",
        contractType: DEFAULT_EMPLOYMENT_CONTRACT_TYPE, startDate: new Date().toISOString().slice(0, 10),
        reportsToId: null, mainRole: "user", subRole: "employee",
        location: companyCountry || "",
      });
      setEmpDetails({ ...defaultEmpDetails });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, departments, companyCountry]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" />
            {t("organization.addEmployeeTitle")}
          </DialogTitle>
          <DialogDescription>{t("organization.addEmployeeDesc")}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
            <FloatField label={t("organization.name")} required value={form.name} onChange={(v) => update("name", v)} />
            <FloatField label={t("organization.email")} type="email" required value={form.email} onChange={(v) => update("email", v)} />
            <FloatWrapper label={`${t("certifications.position", "Position")} *`}>
              <select
                value={form.positionId != null ? String(form.positionId) : ""}
                onChange={(e) => {
                  const posId = e.target.value ? Number(e.target.value) : null;
                  const pos = addPositions.find((p) => p.id === posId);
                  update("positionId", posId);
                  update("jobTitle", pos?.title ?? "");
                }}
                className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm appearance-none">
                <option value="">— {t("certifications.selectPosition", "Select position")} —</option>
                {addPositions.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </FloatWrapper>
            <FloatWrapper label={`${t("organization.department")} *`}>
              <select value={form.department} onChange={(e) => update("department", e.target.value)}
                className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm appearance-none">
                <option value="">{`— ${t("organization.selectDept")} —`}</option>
                {departments.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </FloatWrapper>
            <FloatWrapper label={t("reporting.label")}>
              <select value={form.reportsToId?.toString() ?? ""} onChange={(e) => update("reportsToId", e.target.value ? Number(e.target.value) : null)}
                className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm appearance-none">
                <option value="">{t("reporting.noManager")}</option>
                {employees.filter((emp) => emp.status === "Active" && (form.mainRole !== "hr" || emp.subRole === "c_level")).map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.name} · {emp.jobTitle}{emp.isLineManager ? "" : ` · ${t("organization.notYetLineManager", "not a line manager")}`}</option>
                ))}
              </select>
            </FloatWrapper>
            <FloatWrapper label={t("organization.contractType")}>
              <select value={selectedContractType} onChange={(e) => updateContractType(e.target.value as EmploymentContractTypeValue)}
                className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm appearance-none">
                {EMPLOYMENT_CONTRACT_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>{getContractTypeLabel(option.value, t)}</option>
                ))}
              </select>
            </FloatWrapper>
            <FloatWrapper label={t("organization.location")}>
              <select value={form.location || ""} onChange={(e) => update("location", e.target.value)}
                className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm appearance-none">
                <option value="">{`— ${t("organization.selectCountry")} —`}</option>
                {COUNTRIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </FloatWrapper>
            <FloatField label={t("organization.phone")} type="tel" value={form.phone || ""} onChange={(v) => update("phone", v)} />
            <FloatWrapper label={`${t("organization.startDate")} *`} className="sm:col-span-2">
              <DatePicker value={form.startDate} onChange={(v) => update("startDate", v)} className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm" />
            </FloatWrapper>

            {/* ── Employment Details (conditional per contract type) ── */}
            <div className="sm:col-span-2 border-t pt-4 mt-1">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <Briefcase className="h-3.5 w-3.5" /> {t("organization.addEmployeeEmployment.title")}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
                {/* Work arrangement — all types */}
                <FloatWrapper label={t("organization.addEmployeeEmployment.workArrangement")}>
                  <select value={empDetails.workArrangement || ""} onChange={(e) => updateEmp("workArrangement", e.target.value || null)}
                    className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm appearance-none">
                    <option value="">— {t("organization.addEmployeeEmployment.select")} —</option>
                    <option value="on-site">{t("organization.addEmployeeEmployment.workArrangementOptions.onSite")}</option>
                    <option value="hybrid">{t("organization.addEmployeeEmployment.workArrangementOptions.hybrid")}</option>
                    <option value="remote">{t("organization.addEmployeeEmployment.workArrangementOptions.remote")}</option>
                  </select>
                </FloatWrapper>
                <FloatField label={t("organization.addEmployeeEmployment.careerLevel")} value={empDetails.careerLevel || ""} onChange={(v) => updateEmp("careerLevel", v || null)} />

                {/* Full-time specific */}
                {isFullTimeContractType(selectedContractType) && (<>
                  <FloatWrapper label={t("organization.addEmployeeEmployment.probationEndDate")}>
                    <DatePicker value={empDetails.probationEndDate || ""} onChange={(v) => updateEmp("probationEndDate", v || null)} className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm" />
                  </FloatWrapper>
                  <FloatField label={t("organization.addEmployeeEmployment.noticePeriodDays")} type="number" value={empDetails.noticePeriodDays != null ? String(empDetails.noticePeriodDays) : ""} onChange={(v) => updateEmp("noticePeriodDays", v ? Number(v) : null)} />
                </>)}

                {isFixedTermContract && (
                  <div>
                    <FloatWrapper label={t("organization.addEmployeeEmployment.fixedTermEndDateRequired")}>
                      <DatePicker
                        value={empDetails.contractEndDate || ""}
                        onChange={(v) => updateEmp("contractEndDate", v || null)}
                        className={cn(
                          "w-full rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm",
                          fixedTermEndDateMissing && "border-destructive focus-visible:ring-destructive"
                        )}
                      />
                    </FloatWrapper>
                    {fixedTermEndDateMissing && (
                      <p className="mt-1.5 text-xs font-medium text-destructive">
                        {t("organization.addEmployeeEmployment.fixedTermEndDateMissing")}
                      </p>
                    )}
                  </div>
                )}

              </div>
            </div>

            {isPartTimeContract && (
              <div className="sm:col-span-2 rounded-xl border border-border/70 bg-muted/20 p-4">
                <p className="text-sm font-semibold text-foreground mb-3">
                  {t("organization.addEmployeeEmployment.partTimeDetailsTitle")}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
                  <FloatWrapper
                    label={t("organization.addEmployeeEmployment.partTimeHoursPerDay")}
                    required
                    error={partTimeHoursPerDayError}
                  >
                    <Input
                      type="number"
                      min="0.25"
                      max="7.99"
                      step="0.25"
                      value={empDetails.partTimeHoursPerDay ?? ""}
                      onChange={(e) => updateEmp("partTimeHoursPerDay", e.target.value === "" ? null : Number(e.target.value))}
                      className={cn(
                        "w-full rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm",
                        partTimeHoursPerDayError && "border-destructive focus-visible:ring-destructive"
                      )}
                    />
                  </FloatWrapper>

                  <div className="mt-3 flex min-h-14 items-center gap-3 rounded-xl border border-[#d0d5e8] bg-white dark:bg-card px-4">
                    <Checkbox
                      id="add-part-time-includes-saturday"
                      checked={!!empDetails.partTimeIncludesSaturday}
                      onCheckedChange={(checked) => updateEmp("partTimeIncludesSaturday", checked === true)}
                    />
                    <Label htmlFor="add-part-time-includes-saturday" className="text-sm font-medium">
                      {t("organization.addEmployeeEmployment.partTimeIncludesSaturday")}
                    </Label>
                  </div>

                  {partTimeWeeklyHours != null && (
                    <p className="sm:col-span-2 text-sm text-muted-foreground">
                      {t("organization.addEmployeeEmployment.partTimeWeeklyAllocatedHours", { hours: partTimeWeeklyHours })}
                    </p>
                  )}
                </div>
              </div>
            )}
            <FloatWrapper label={t("organization.systemRoleLabel")} className="sm:col-span-2">
              <select value={form.mainRole} onChange={(e) => {
                const mr = e.target.value;
                update("mainRole", mr);
                if (mr === "hr" && !employees.some(emp => Number(emp.id) === form.reportsToId && emp.subRole === "c_level")) update("reportsToId", null);
                update("subRole", mr === "user" ? "employee" : null);
              }} className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm appearance-none">
                <option value="user">{t("organization.userTypeEmployee")}</option>
                <option value="hr">{t("organization.hrOption")}</option>
                <option value="admin">{t("organization.adminOption")}</option>
              </select>
            </FloatWrapper>
            {form.mainRole === "user" && (
              <FloatWrapper label={t("organization.employeeType")} className="sm:col-span-2">
                <select value={form.subRole ?? "employee"} onChange={(e) => update("subRole", e.target.value)}
                  className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm appearance-none">
                  <option value="employee">{t("organization.userTypeEmployee")}</option>
                  <option value="line_manager">{t("organization.lineManagerOption")}</option>
                  <option value="c_level">{t("organization.userTypeCLevel")}</option>
                </select>
              </FloatWrapper>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t shrink-0">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>{t("common.cancel")}</Button>
          <Button
            onClick={async () => {
              if (fixedTermEndDateMissing || partTimeDetailsInvalid) return;
              setIsSubmitting(true);
              try {
                await onSubmit({
                  ...form,
                  contractType: selectedContractType,
                  employmentDetails: buildEmploymentDetailsPayload(),
                });
              } finally {
                setIsSubmitting(false);
              }
            }}
            disabled={!form.name || !form.email || !form.positionId || !form.department || !form.startDate || fixedTermEndDateMissing || partTimeDetailsInvalid || isSubmitting}
          >
            {isSubmitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <UserPlus className="h-4 w-4 mr-1" />}
            {isSubmitting ? t("common.creating", "Creating…") : t("organization.addEmployee")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Employee Edit Sheet
// ============================================================

function EmployeeEditSheet({ employee, open, onClose, onStatusChange, onDeactivate, isAdmin, t }: {
  employee: EmployeeListItem | null; open: boolean;
  onClose: () => void;
  onStatusChange: (id: number, status: string) => void;
  onDeactivate: (id: number) => void;
  isAdmin?: boolean;
  t: any;
}) {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isHr = user?.role.mainRole === "hr";
  const canSeeFullProfile = isAdmin || isHr;
  const [userRole, setUserRole] = useState<EmployeeUserRole | null | "none">(null);
  const [roleForm, setRoleForm] = useState<{ mainRole: string; subRole: string | null }>({ mainRole: "user", subRole: "employee" });
  const [pendingStatus, setPendingStatus] = useState<string>("Active");
  const [savingAdmin, setSavingAdmin] = useState(false);
  const [statusConfirmTarget, setStatusConfirmTarget] = useState<string | null>(null);

  // Full profile object — drives the reused ProfileHeader + tab components.
  // Shares the ["employee", id] cache with EmployeeProfile.tsx so edits stay in sync.
  const { data: fullEmployee, isLoading: loadingProfile } = useQuery({
    queryKey: ["employee", employee?.id],
    queryFn: () => apiGetEmployee(employee!.id),
    enabled: !!employee && open,
  });

  // Keep the pending status mirror in sync with the server value
  useEffect(() => {
    if (fullEmployee?.status) setPendingStatus(fullEmployee.status);
  }, [fullEmployee?.status]);

  useEffect(() => {
    if (employee && isAdmin) {
      setUserRole(null);
      apiGetEmployeeUserRole(employee.email).then((role) => {
        if (role) {
          setUserRole(role);
          setRoleForm({ mainRole: role.mainRole, subRole: role.subRole });
        } else {
          setUserRole("none");
        }
      }).catch(() => setUserRole("none"));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee?.email, isAdmin]);

  if (!employee) return null;

  // ── Admin controls dirty tracking (role + status) ──
  const normSub = (mr: string, sr: string | null) => (mr === "user" ? (sr || "employee") : null);
  const roleDirty = !!(isAdmin && userRole && userRole !== "none" &&
    (roleForm.mainRole !== userRole.mainRole ||
      normSub(roleForm.mainRole, roleForm.subRole) !== normSub(userRole.mainRole, userRole.subRole)));
  const statusDirty = !!fullEmployee && pendingStatus !== fullEmployee.status;
  const adminDirty = roleDirty || statusDirty;

  const handleAdminSave = async () => {
    setSavingAdmin(true);
    try {
      if (statusDirty) {
        await onStatusChange(parseInt(employee.id), pendingStatus);
      }
      if (roleDirty) {
        const updated = await apiUpdateEmployeeUserRole(employee.email, roleForm.mainRole, roleForm.mainRole === "user" ? roleForm.subRole : null);
        setUserRole(updated);
        setRoleForm({ mainRole: updated.mainRole, subRole: updated.subRole });
      }
      await queryClient.invalidateQueries({ queryKey: ["employee", employee.id] });
      toast({ title: t("profile.editSuccess"), description: t("profile.editSuccessDesc") });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
    setSavingAdmin(false);
  };

  const handleAdminCancel = () => {
    if (fullEmployee?.status) setPendingStatus(fullEmployee.status);
    if (userRole && userRole !== "none") setRoleForm({ mainRole: userRole.mainRole, subRole: userRole.subRole });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-6xl w-[97vw] max-h-[94vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader className="sr-only">
          <DialogTitle>{employee.name}</DialogTitle>
          <DialogDescription>{employee.jobTitle} &bull; {employee.department}</DialogDescription>
        </DialogHeader>

        {loadingProfile || !fullEmployee ? (
          <div className="py-24 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />{t("common.loading")}
          </div>
        ) : (
          <div className="space-y-5 pt-5">
            {/* Profile card — avatar upload, identity, status, meta */}
            <ProfileHeader
              employee={fullEmployee}
              headerExtra={
                <div className="flex items-center justify-end gap-2 mt-1">
                  {/* Status — toggle switch */}
                  <button
                    type="button"
                    role="switch"
                    aria-checked={pendingStatus === "Active"}
                    onClick={() => setStatusConfirmTarget(pendingStatus === "Active" ? "Disabled" : "Active")}
                    className={cn(
                      "relative inline-flex h-7 w-[3.25rem] shrink-0 items-center rounded-full border transition-colors cursor-pointer",
                      pendingStatus === "Active"
                        ? "bg-emerald-500 border-emerald-500"
                        : "bg-muted border-border"
                    )}
                    title={pendingStatus === "Active" ? t("common.active") : t("common.disabled")}
                  >
                    <span
                      className={cn(
                        "inline-flex items-center justify-center h-5 w-5 rounded-full bg-white shadow transition-transform",
                        pendingStatus === "Active" ? "translate-x-[1.625rem]" : "translate-x-0.5"
                      )}
                    >
                      {pendingStatus === "Active" ? (
                        <Check className="h-3 w-3 text-emerald-500" />
                      ) : (
                        <X className="h-3 w-3 text-muted-foreground" />
                      )}
                    </span>
                  </button>

                  {/* Role selector — admin only */}
                  {isAdmin && userRole && userRole !== "none" && (
                    <div className="flex items-center gap-1.5">
                      <KeyRound className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <select
                        value={roleForm.mainRole}
                        onChange={(e) => {
                          const mr = e.target.value;
                          setRoleForm({ mainRole: mr, subRole: mr === "user" ? "employee" : null });
                        }}
                        className="h-8 rounded-lg border border-input bg-background px-2 text-xs cursor-pointer"
                      >
                        <option value="admin">{t("organization.roleAdmin")}</option>
                        <option value="hr">{t("organization.roleHr")}</option>
                        <option value="user">{t("organization.roleUser")}</option>
                      </select>
                      {roleForm.mainRole === "user" && (
                        <select
                          value={roleForm.subRole || "employee"}
                          onChange={(e) => setRoleForm((p) => ({ ...p, subRole: e.target.value }))}
                          className="h-8 rounded-lg border border-input bg-background px-2 text-xs cursor-pointer"
                        >
                          <option value="employee">{t("organization.userTypeEmployee")}</option>
                          <option value="c_level">{t("organization.userTypeCLevel")}</option>
                        </select>
                      )}
                    </div>
                  )}
                  {isAdmin && userRole === "none" && (
                    <span className="text-[10px] text-muted-foreground italic">{t("organization.noSystemAccount")}</span>
                  )}
                </div>
              }
            />

            <Tabs defaultValue="about" className="w-full">
              <TabsList className="w-full justify-start bg-transparent h-auto p-0 border-b border-border/60 rounded-none gap-0 overflow-x-auto overflow-y-hidden">
                <TabsTrigger value="about" className="gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
                  <User className="h-3.5 w-3.5" />{t("profile.tabs.about")}
                </TabsTrigger>
                {canSeeFullProfile && (
                  <>
                    <TabsTrigger value="personal" className="gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
                      <FileText className="h-3.5 w-3.5" />{t("profile.tabs.personalInfo")}
                    </TabsTrigger>
                    <TabsTrigger value="performance" className="gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
                      <BarChart3 className="h-3.5 w-3.5" />{t("profile.tabs.performance")}
                    </TabsTrigger>
                    <TabsTrigger value="salary" className="gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
                      <DollarSign className="h-3.5 w-3.5" />{t("profile.tabs.salary")}
                    </TabsTrigger>
                    <TabsTrigger value="documents" className="gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
                      <FolderOpen className="h-3.5 w-3.5" />{t("profile.tabs.documents")}
                    </TabsTrigger>
                    <TabsTrigger value="timeoff" className="gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
                      <CalendarDays className="h-3.5 w-3.5" />{t("profile.tabs.timeOff")}
                    </TabsTrigger>
                    <TabsTrigger value="notes" className="gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
                      <MessageSquare className="h-3.5 w-3.5" />{t("profile.tabs.notes")}
                    </TabsTrigger>
                  </>
                )}
                {isAdmin && (
                  <TabsTrigger value="audit" className="gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
                    <History className="h-3.5 w-3.5" />{t("profile.tabs.auditLog")}
                  </TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="about" className="mt-4">
                <ProfileAboutTab employee={fullEmployee} isPublicOnly={!canSeeFullProfile} />
              </TabsContent>

              {canSeeFullProfile && (
                <>
                  <TabsContent value="personal" className="mt-4">
                    <ProfilePersonalInfoTab employee={fullEmployee} isOwnProfile={false} singleEditMode />
                  </TabsContent>
                  <TabsContent value="performance" className="mt-4">
                    <ProfilePerformanceTab employee={fullEmployee} canManage />
                  </TabsContent>
                  <TabsContent value="salary" className="mt-4">
                    <ProfileSalaryTab employee={fullEmployee} isOwnProfile={false} />
                  </TabsContent>
                  <TabsContent value="documents" className="mt-4">
                    <ProfileDocumentsTab employee={fullEmployee} />
                  </TabsContent>
                  <TabsContent value="timeoff" className="mt-4">
                    <ProfileTimeOffTab employee={fullEmployee} />
                  </TabsContent>
                  <TabsContent value="notes" className="mt-4">
                    <ProfileNotesTab employee={fullEmployee} />
                  </TabsContent>
                </>
              )}

              {isAdmin && (
                <TabsContent value="audit" className="mt-4">
                  <ProfileAuditTab employee={fullEmployee} />
                </TabsContent>
              )}
            </Tabs>

            {/* Floating Save bar for header admin changes (role / status) */}
            {adminDirty && (
              <div className="sticky bottom-3 z-30 flex justify-end pointer-events-none">
                <div className="pointer-events-auto flex items-center gap-2 rounded-xl border border-border bg-background/95 backdrop-blur px-3 py-2 shadow-lg">
                  <Button variant="outline" size="sm" onClick={handleAdminCancel} disabled={savingAdmin}>
                    <X className="h-3.5 w-3.5 mr-1" />{t("common.cancel")}
                  </Button>
                  <Button size="sm" onClick={handleAdminSave} disabled={savingAdmin}>
                    {savingAdmin ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                    {savingAdmin ? t("common.saving") : t("common.save")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-2 flex flex-col sm:flex-row gap-3">
          <div className="flex w-full items-center justify-start">
            <Link to={`/employees/${employee.id}`}
              className="text-sm text-primary hover:text-primary/80 font-medium flex items-center gap-1.5 transition-colors">
              {t("employees.viewProfile", "View Full Profile")}
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="flex flex-1 gap-2 sm:justify-end">
            <Button variant="outline" onClick={onClose}>{t("common.close", "Close")}</Button>
          </div>
        </DialogFooter>
      </DialogContent>

      {/* Confirm activating/deactivating the user */}
      <AlertDialog open={statusConfirmTarget !== null} onOpenChange={(v) => { if (!v) setStatusConfirmTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {statusConfirmTarget === "Active"
                ? t("organization.editSheet.confirmActivateTitle")
                : t("organization.editSheet.confirmDeactivateTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {statusConfirmTarget === "Active"
                ? t("organization.editSheet.confirmActivateDesc", { name: employee.name })
                : t("organization.editSheet.confirmDeactivateDesc", { name: employee.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setStatusConfirmTarget(null)}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (statusConfirmTarget) setPendingStatus(statusConfirmTarget);
                setStatusConfirmTarget(null);
              }}
            >
              {statusConfirmTarget === "Active" ? t("organization.activate") : t("organization.deactivate")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

// ============================================================
// Department Edit Sheet
// ============================================================

function DepartmentEditSheet({ department, open, onClose, employees, onSave, onDelete, t }: {
  department: Department | null; open: boolean;
  onClose: () => void;
  employees: EmployeeListItem[];
  onSave: (id: number, data: { name?: string; description?: string; headEmployeeId?: number | null; icon?: string | null }, memberChanges: { toAdd: string[]; toRemove: string[] }) => void;
  onDelete: (dept: Department) => void;
  t: any;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [headId, setHeadId] = useState<string>("");
  const [icon, setIcon] = useState<string | null>(null);
  const [memberIds, setMemberIds] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");

  useEffect(() => {
    if (department) {
      setName(department.name);
      setDescription(department.description || "");
      setHeadId(department.headEmployeeId?.toString() || "");
      setIcon(department.icon || null);
      // Seed checked employees from current department membership
      const current = employees
        .filter((e) => e.department === department.name && e.status !== "Disabled")
        .map((e) => e.id);
      setMemberIds(new Set(current));
    }
  }, [department, employees]);

  if (!department) return null;

  const activeEmployees = employees.filter((e) => e.status === "Active");
  const headEmployee = activeEmployees.find((e) => e.id === headId);

  const handleSave = async () => {
    setSaving(true);
    try {
      const originalIds = new Set(
        employees
          .filter((e) => e.department === department.name)
          .map((e) => e.id),
      );
      const toAdd = [...memberIds].filter((id) => !originalIds.has(id));
      const toRemove = [...originalIds].filter((id) => !memberIds.has(id));

      await onSave(
        department.id,
        {
          name: name.trim(),
          description: description.trim() || undefined,
          headEmployeeId: headId ? parseInt(headId) : null,
          icon,
        },
        { toAdd, toRemove },
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleMember = (id: string, checked: boolean) => {
    setMemberIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  const lc = memberSearch.toLowerCase();
  const filteredEmployees = activeEmployees
    .filter((e) => !lc || e.name.toLowerCase().includes(lc) || (e.jobTitle || "").toLowerCase().includes(lc))
    .sort((a, b) => {
      const aIn = memberIds.has(a.id) ? 0 : 1;
      const bIn = memberIds.has(b.id) ? 0 : 1;
      return aIn - bIn;
    });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-md shrink-0">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <DialogTitle>{department.name}</DialogTitle>
              <DialogDescription>{t("organization.selectedCount", { count: memberIds.size })}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Two-column body */}
        <div className="flex-1 flex overflow-hidden min-h-0">

          {/* ── Left: department details ── */}
          <div className="flex flex-col w-80 shrink-0 border-r border-border overflow-y-auto px-5 py-5 gap-5">

            {/* Department Name */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">{t("organization.departmentName")}</label>
              <div className="flex items-center gap-2">
                <Input value={name} onChange={(e) => setName(e.target.value)} className="h-10" />
                <DecorativeIconPicker
                  variant="inline"
                  value={icon}
                  defaultIconKey={getDefaultDepartmentIconKey(name || department.name)}
                  onSelect={(key) => setIcon(key)}
                  className="h-10 w-10 rounded-xl border border-input"
                />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">{t("organization.description")}</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("organization.deptDescPlaceholder")}
                rows={3}
                className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {/* Department Head */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">{t("organization.departmentHead")}</label>
              <select
                value={headId}
                onChange={(e) => {
                  setHeadId(e.target.value);
                  if (e.target.value) setMemberIds((prev) => { const n = new Set(prev); n.add(e.target.value); return n; });
                }}
                className="w-full rounded-xl border border-[#d0d5e8] h-10 px-3 text-sm appearance-none"
              >
                <option value="">{t("organization.noHead")}</option>
                {activeEmployees.map((e) => (
                  <option key={e.id} value={e.id}>{e.name}{e.jobTitle ? ` — ${e.jobTitle}` : ""}</option>
                ))}
              </select>
              {headId && headEmployee && (
                <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                  <div className={`h-7 w-7 rounded-full bg-gradient-to-br ${headEmployee.avatarColor} flex items-center justify-center text-white text-[10px] font-semibold shrink-0`}>
                    {headEmployee.initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{headEmployee.name}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{headEmployee.jobTitle}</p>
                  </div>
                  <Crown className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                </div>
              )}
            </div>

            {/* Delete — pushed to bottom */}
            <div className="mt-auto pt-4 border-t">
              <button
                onClick={() => onDelete(department)}
                className="text-xs text-red-600 hover:text-red-700 font-medium flex items-center gap-1.5 transition-colors"
              >
                <Trash2 className="h-3.5 w-3.5" />{t("organization.deleteDepartment")}
              </button>
            </div>
          </div>

          {/* ── Right: members ── */}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
            {/* Members header + search */}
            <div className="px-5 pt-5 pb-3 shrink-0 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">
                  {t("organization.members")}
                  <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                    ({t("organization.selectedCount", { count: memberIds.size })})
                  </span>
                </span>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  placeholder={t("organization.searchEmployees", "Search employees…")}
                  className="w-full h-9 pl-8 pr-3 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
                {memberSearch && (
                  <button
                    onClick={() => setMemberSearch("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Scrollable member list */}
            <div className="flex-1 overflow-y-auto border-t border-border divide-y divide-border">
              {filteredEmployees.length === 0 ? (
                <p className="text-sm text-muted-foreground italic px-5 py-6 text-center">
                  {memberSearch ? t("organization.noResults", "No employees match your search") : t("organization.noMembers")}
                </p>
              ) : (
                filteredEmployees.map((emp) => {
                  const isHead = emp.id === headId;
                  return (
                    <label key={emp.id} className="flex items-center gap-3 px-5 py-2.5 cursor-pointer hover:bg-secondary/40 transition-colors">
                      <Checkbox
                        checked={memberIds.has(emp.id)}
                        onCheckedChange={(checked) => toggleMember(emp.id, !!checked)}
                      />
                      <div className={`h-7 w-7 rounded-full bg-gradient-to-br ${emp.avatarColor} flex items-center justify-center text-white text-[9px] font-semibold shrink-0`}>
                        {emp.initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate leading-none">{emp.name}</p>
                        {emp.jobTitle && <p className="text-xs text-muted-foreground mt-0.5 truncate">{emp.jobTitle}</p>}
                      </div>
                      {isHead && <Crown className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
                    </label>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t shrink-0">
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={handleSave} disabled={saving || !name.trim()} className="gap-2">
            <Save className="h-4 w-4" />{saving ? t("common.saving") : t("organization.saveChanges")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Add Department Dialog
// ============================================================

function AddDepartmentDialog({ open, onClose, employees, onSubmit, t }: {
  open: boolean; onClose: () => void;
  employees: EmployeeListItem[];
  onSubmit: (data: { name: string; description?: string; headEmployeeId?: number; memberIds?: string[]; icon?: string }) => Promise<void>;
  t: any;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [headId, setHeadId] = useState("");
  const [icon, setIcon] = useState<string | null>(null);
  const [memberIds, setMemberIds] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const activeEmployees = employees.filter((e) => e.status === "Active");
  const headEmployee = activeEmployees.find((e) => e.id === headId);

  useEffect(() => {
    if (open) {
      setName("");
      setDescription("");
      setHeadId("");
      setIcon(null);
      setMemberIds(new Set());
      setIsSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit({
        name: name.trim(),
        description: description.trim() || undefined,
        headEmployeeId: headId ? parseInt(headId) : undefined,
        memberIds: [...memberIds],
        icon: icon || undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMember = (id: string, checked: boolean) => {
    setMemberIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle>{t("organization.addDepartment")}</DialogTitle>
          <DialogDescription>{t("organization.addDeptDesc")}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">

          {/* Department Name */}
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <FloatField
                label={`${t("organization.departmentName")} *`}
                value={name}
                onChange={(v) => setName(v as string)}
                placeholder="e.g. Engineering, HR…"
              />
            </div>
            <DecorativeIconPicker
              variant="inline"
              value={icon}
              defaultIconKey={getDefaultDepartmentIconKey(name)}
              onSelect={(key) => setIcon(key)}
              className="h-14 w-14 rounded-xl border border-input mb-0"
            />
          </div>

          {/* Description */}
          <FloatField
            label={t("organization.description")}
            type="textarea"
            value={description}
            onChange={(v) => setDescription(v as string)}
            placeholder={t("organization.deptDescPlaceholder")}
          />

          {/* Department Head */}
          <FloatWrapper label={t("organization.departmentHead")}>
            <select
              value={headId}
              onChange={(e) => {
                setHeadId(e.target.value);
                if (e.target.value) setMemberIds((prev) => { const n = new Set(prev); n.add(e.target.value); return n; });
              }}
              className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm appearance-none"
            >
              <option value="">{t("organization.noHead")}</option>
              {activeEmployees.map((e) => (
                <option key={e.id} value={e.id}>{e.name}{e.jobTitle ? ` — ${e.jobTitle}` : ""}</option>
              ))}
            </select>
            {headId && headEmployee && (
              <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                <div className={`h-7 w-7 rounded-full bg-gradient-to-br ${headEmployee.avatarColor} flex items-center justify-center text-white text-[10px] font-semibold`}>
                  {headEmployee.initials}
                </div>
                <div>
                  <p className="text-xs font-medium">{headEmployee.name}</p>
                  <p className="text-[10px] text-muted-foreground">{headEmployee.jobTitle}</p>
                </div>
                <Crown className="h-3.5 w-3.5 text-amber-500 ml-auto" />
              </div>
            )}
          </FloatWrapper>

          {/* Members */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {t("organization.members")}{" "}
              <span className="text-muted-foreground font-normal text-xs">
                ({t("organization.optionalMembers")}{memberIds.size > 0 ? `, ${t("organization.selectedCount", { count: memberIds.size })}` : ""})
              </span>
            </label>
            <div className="border border-border rounded-xl divide-y divide-border max-h-52 overflow-y-auto">
              {activeEmployees.length === 0 ? (
                <p className="text-sm text-muted-foreground italic px-3 py-4 text-center">{t("organization.noMembers")}</p>
              ) : (
                activeEmployees.map((emp) => {
                  const isHead = emp.id === headId;
                  return (
                    <label key={emp.id} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-secondary/40 transition-colors">
                      <Checkbox
                        checked={memberIds.has(emp.id)}
                        onCheckedChange={(checked) => toggleMember(emp.id, !!checked)}
                      />
                      <div className={`h-6 w-6 rounded-full bg-gradient-to-br ${emp.avatarColor} flex items-center justify-center text-white text-[9px] font-semibold shrink-0`}>
                        {emp.initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate leading-none">{emp.name}</p>
                        {emp.jobTitle && <p className="text-xs text-muted-foreground mt-0.5 truncate">{emp.jobTitle}</p>}
                      </div>
                      {isHead && <Crown className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
                    </label>
                  );
                })
              )}
            </div>
          </div>

        </div>

        <DialogFooter className="px-6 py-4 border-t shrink-0">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>{t("common.cancel")}</Button>
          <Button onClick={handleSubmit} disabled={!name.trim() || isSubmitting} className="gap-2">
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {isSubmitting ? t("common.creating", "Creating…") : t("organization.addDepartment")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Delete Department Dialog
// ============================================================

function DeleteDepartmentDialog({ open, onClose, department, departments, onConfirm, t }: {
  open: boolean; onClose: () => void;
  department: Department | null;
  departments: Department[];
  onConfirm: (deptId: number, reassignTo?: string) => void;
  t: any;
}) {
  const [reassignTo, setReassignTo] = useState("");
  const otherDepts = departments.filter((d) => d.id !== department?.id);

  if (!department) return null;

  const hasEmployees = department.employeeCount > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="">
        <DialogHeader>
          <DialogTitle className="text-red-600 flex items-center gap-2">
            <Trash2 className="h-4 w-4" />{t("organization.deleteDepartment")}
          </DialogTitle>
          <DialogDescription>
            {hasEmployees
              ? t("organization.deleteDeptWithEmployees", { name: department.name, count: department.employeeCount })
              : t("organization.deleteDeptConfirm", { name: department.name })}
          </DialogDescription>
        </DialogHeader>
        {hasEmployees && (
          <div className="py-2">
            <FloatWrapper label={t("organization.reassignTo")}>
              <select value={reassignTo} onChange={(e) => setReassignTo(e.target.value)}
                className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm appearance-none">
                <option value="">{t("organization.selectDepartment")}</option>
                {otherDepts.map((d) => <option key={d.id} value={d.name}>{d.name} ({d.employeeCount})</option>)}
              </select>
            </FloatWrapper>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="destructive" onClick={() => onConfirm(department.id, reassignTo || undefined)}
            disabled={hasEmployees && !reassignTo} className="gap-2">
            <Trash2 className="h-4 w-4" />{t("organization.confirmDelete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default Organization;
